// CoordinationGantt — dependency-free SVG gantt for the BIM
// coordination pages. Fixed 6-week window (2 back, 4 forward), one row
// per plan item (children indented under parentId, or one lane per
// contractor via the grouping toggle), bars colored by healthStatus,
// milestones as diamonds, meetings as recurrence-expanded circles,
// finish-to-start dependency arrows (red + ⚠ when the predecessor is
// behind), a "today" line, week gridlines, and a red open-issue badge
// to the right of each bar. Parents (and contractor lanes) collapse /
// expand via the small triangle in front of the Activity cell (Expand
// all / Collapse all in the toolbar); the collapsed set is remembered
// per project in localStorage.
//
// Props:
//   items             — feed items [{_id,type,name,parentId,startDate,
//                       endDate,status,healthStatus,issueCounts,
//                       recurrence,progress,dependencies,
//                       responsibleCompanyId,sortIndex}]
//   onSelect          — (item) => void, row click
//   projectId         — required for any PATCHing (drag / progress)
//   editable          — true, or a predicate (item)=>boolean when only
//                       SOME rows may be dragged (a partner with
//                       allowPlanEdit: their own company's rows).
//                       Owner-side drag: move marks horizontally, drag a
//                       task bar's right edge to resize; drops PATCH
//                       startDate/endDate. Default false, so existing
//                       read-only usages are unaffected.
//   onChanged         — (updatedItem) => void after a successful PATCH
//                       (parents typically refetch the feed)
//   companyNames      — optional {companyId: name} map used as labels by
//                       the "By contractor" grouping
//   canReportProgress — optional (item) => bool; when the chart is NOT
//                       editable, matching task bars open a slider
//                       popover that PATCHes {progress} only
//   onClaim           — optional (item) => void; renders a "Claim" chip on
//                       rows the server flagged `claimable` (work assigned
//                       to no company). Partner side only - the owner
//                       assigns directly. Rows flagged `redacted` belong to
//                       another trade and arrive with no name at all.
//   onToggleDisabled  — optional (item, disabled) => void; when given (or
//                       when editable with a projectId) the left table gets
//                       a checkbox column: ticked = activity disabled -
//                       shown barred like a completed row, left out of the
//                       email snapshot / health / reminders, its issues and
//                       checklist items abandoned while disabled
//   onEditItem        — optional (item) => void; when given, the left table
//                       gets an extra pen column (one per row). Callers pass
//                       it only when the viewer may edit the schedule
//                       (server-computed can.ganttEdit: owner company + key)
//   canEditProgress   — optional bool or (item)=>bool: the % in front of a
//                       LEAF row's name becomes an inline editor (PUT
//                       /gantt-items/:gid/progress). Callers pass
//                       progressEditRule(can, myCompany) from ProgressCell:
//                       true (every leaf) or a predicate (a partner with
//                       allowPlanEdit: their own company's rows, the same
//                       rows `editable` lets them drag). Parents always read the
//                       average of their tasks and are never editable.
//   filterText        — optional controlled search text: rows whose name /
//                       WBS / type / status / contractor / discipline /
//                       area match are shown (with their ancestors and
//                       children); when omitted the toolbar shows its
//                       own search box
import { partnerTradeIndex, tradesText, partnersText } from "components/Partners/partnerTrades";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { forcedSignOff } from "./signOffForced";
import GanttContextMenu from "./GanttContextMenu";
import { rowDragHandlers } from "./rowDragDrop";
import { Segmented, Slider, Button, Input, Tooltip, message } from "antd";
import AreaTooltip from "components/Coordination/AreaTooltip";
import AreaShapePreview from "components/Coordination/AreaShapePreview";
import DateRangeFilter from "components/Coordination/DateRangeFilter";
import { utcDayKey, inDayRange, rangeEmpty } from "helpers/dateRange";
import { todayKey, runsToday, madeToday, dueTodayItem, emptyMessage, ownerStateOfItem } from "./ganttDayFilters";
import OwnerSignOffFilter from "components/Coordination/OwnerSignOffFilter";
import { EditOutlined, FullscreenOutlined, FullscreenExitOutlined } from "@ant-design/icons";
import { updateGanttItem, setGanttItemProgress } from "api/gantt/ganttApi";
import ProgressCell, { parentIdsOf, pctText, pctDisplay, PARENT_TIP } from "./ProgressCell";
import { issueLabel } from "helpers/artifactPrefix";
import { usePreference } from "helpers/userPrefs";
import {
  DAY_MS, PX_PER_DAY as DEFAULT_PX_PER_DAY, ROW_H, HEADER_H,
  WEEKEND_BAND_MIN_PX_PER_DAY, weekendDays,
  HEALTH_FILL, DONE_FILL, PHASE_LABEL, gateTag,
  startOfDay, dayDiff, shiftDays, toIsoDate, fmt,
  flattenTree, buildCompanyRows, buildGroupedRows, expandOccurrences, depArrowPath,
} from "./ganttHelpers";
import { isMilestone, isMeeting } from "helpers/schedule/activityTypes";
import { depId, depType, depLag } from "helpers/schedule/predecessors";
import { issueOnActivity } from "helpers/schedule/issueActivityIds";

// Hover card for the chart, positioned around the pointer.
//
// It is absolutely positioned inside the gantt root, and that root sets
// only `position: relative` — no overflow of its own. So a card opened on
// one of the LAST rows ran past the root's bottom edge and grew the
// page's scroll height: a scrollbar appeared, the layout reflowed under
// the pointer, the hover re-measured, the card moved, the page shrank
// again. That loop is the scroll/unscroll flicker on the bottom rows.
//
// The card therefore measures itself after paint and flips ABOVE the
// pointer whenever there is not enough room below it, so it never
// extends past the chart in the first place. Same idea horizontally,
// which the card already did near the right edge.
function HoverCard({ x, y, cardW, rootW, rootH, accent, children }) {
  const ref = useRef(null);
  const [h, setH] = useState(0);
  // Layout effect, not useEffect: the correction has to happen before the
  // browser paints, or the card shows once in the wrong place and hops.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && el.offsetHeight !== h) setH(el.offsetHeight);
  });

  const GAP = 18;
  // Right of the pointer; flip to its left near the chart's right edge.
  const left = x + 16 + cardW > rootW - 4 ? Math.max(0, x - 16 - cardW) : x + 16;
  // Below the pointer; above it when the card would run past the bottom.
  // Clamped at 0 so a card taller than the chart still starts inside it
  // rather than being dragged off the top.
  const below = y + GAP;
  const flip = h > 0 && rootH > 0 && below + h > rootH;
  const top = flip ? Math.max(0, y - GAP - h) : below;

  return (
    <div
      ref={ref}
      style={{
        position: "absolute", left, top, zIndex: 1000, width: cardW, maxWidth: "95%",
        background: "#fff", border: "1px solid #e5e7eb",
        borderLeft: `5px solid ${accent || "#1E88E5"}`, borderRadius: 10,
        boxShadow: "0 12px 32px rgba(0,0,0,0.16)", padding: "12px 14px",
        fontSize: 13, color: "#333", pointerEvents: "none",
        // Hidden for the single frame between mount and the first
        // measurement, so it is never painted at the unflipped position.
        visibility: h ? "visible" : "hidden",
      }}
    >
      {children}
    </div>
  );
}

// Left-hand table columns (resizable). Widths in px.
const LEFT_COLS = [
  { key: "name", title: "Activity" },
  { key: "start", title: "Start" },
  { key: "end", title: "End" },
  { key: "company", title: "Responsible / contractor" },
  { key: "status", title: "Status" },
  { key: "updated", title: "Modified" },
];
const DEFAULT_COL_WIDTHS = { name: 240, start: 84, end: 84, company: 200, status: 90, updated: 92 };
// Extra fixed-width column (a pen per row) shown only when the caller
// passes onEditItem, i.e. the viewer may edit the schedule.
const EDIT_COL = { key: "edit", title: "", fixed: true };
const EDIT_COL_W = 30;
const DISABLE_COL = { key: "disable", title: "", fixed: true, w: 30 };
const STATUS_LABEL = { planned: "Planned", in_progress: "In progress", done: "Done", on_hold: "On hold" };
// Five-point star centred on (cx, cy): outer radius R, inner radius r.
// The sign-off marker - a shape nothing else on the chart uses.
const starPoints = (cx, cy, R, r) => Array.from({ length: 10 }, (_, i) => {
  const a = -Math.PI / 2 + (i * Math.PI) / 5;
  const rad = i % 2 === 0 ? R : r;
  return `${(cx + rad * Math.cos(a)).toFixed(1)},${(cy + rad * Math.sin(a)).toFixed(1)}`;
}).join(" ");

const TYPE_LABEL = { task: "Task", milestone: "Milestone", meeting: "Meeting", signoff: "Sign-off", area_release: "Area release" };
// Small expand / collapse triangle drawn in front of the Activity cell.
// Rows without children get an invisible spacer of the same width so
// sibling names stay aligned. Clicks don't bubble to the row (which
// would open the item).
function Caret({ open, visible, onToggle }) {
  return (
    <span
      role={visible ? "button" : undefined}
      aria-label={visible ? (open ? "Collapse" : "Expand") : undefined}
      aria-expanded={visible ? open : undefined}
      title={visible ? (open ? "Collapse" : "Expand") : undefined}
      onClick={visible && onToggle ? (e) => { e.stopPropagation(); onToggle(); } : undefined}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        display: "inline-block", width: 14, marginRight: 2, textAlign: "center", flex: "0 0 auto",
        fontSize: 9, lineHeight: 1, color: "#8c8c8c", cursor: visible ? "pointer" : "default",
        visibility: visible ? "visible" : "hidden", userSelect: "none",
      }}
    >
      {open ? "\u25BC" : "\u25B6"}
    </span>
  );
}
// "2026-09-11" as that calendar day here (new Date of the string is UTC
// midnight, the evening before in the Americas).
const ymdLocal = (v) => { const [y, m, d] = String(v || "").slice(0, 10).split("-").map(Number); return new Date(y, (m || 1) - 1, d || 1); };
const fmtLong = (d) => (d && !isNaN(+d) ? d.toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric" }) : "—");

// A row may be a DRAFT row (the schedule editor works in its own keys),
// in which case its coordination areas are keyed on the activity it came
// from, not on the draft key.
function makeAreasFor(map) {
  return (item) => {
    const m = map || {};
    return m[String(item && item.srcId)] || m[String(item && item._id)] || [];
  };
}

export default function CoordinationGantt({
  items,
  onSelect,
  projectId,
  editable = false,
  onChanged,
  // Draft mode (Schedule page): drags call this with (itemId, {startDate,
  // endDate}) instead of PATCHing the server - the caller owns the rows.
  onLocalChange,
  // Draft mode only (Schedule page edit). Right-click an activity on the
  // chart -> (itemId, action, { date }) with action one of start | finish |
  // signoff | rename | delete | after | before; date = the day under the
  // cursor (null from the left table). Drag a row in the left table onto
  // another -> onMoveRow(dragId, targetId, "above" | "below").
  onContextAction,
  onMoveRow,
  companyNames,
  canReportProgress,
  // { ganttItemId: [area] } from the gantt feed - lets a row show WHERE
  // it is on the drawing without leaving the schedule.
  areasByItem = {},
  // Due-date marker layers (both optional): issues and checklist items
  // linked to plan rows render small glyphs at their due dates.
  issues = [],
  checklistItems = [],
  filterText,
  todayOnly,
  onEditItem,
  onToggleDisabled,
  onClaim,
  canEditProgress,
}) {
  const areasFor = useMemo(() => makeAreasFor(areasByItem), [areasByItem]);
  // Remembered per person (helpers/userPrefs, owner 2026-09-18: "by user").
  const [groupMode, setGroupMode] = usePreference("gantt.groupMode", "wbs");
  // Search: controlled by the parent (filterText) or by the toolbar box.
  const [ownFilter, setOwnFilter] = useState("");
  const query = String(filterText !== undefined ? filterText : ownFilter).trim().toLowerCase();
  // "Today": only activities running today (start <= today <= end) or
  // overdue and not done - controlled by the parent or the toolbar button.
  const [ownToday, setOwnToday] = useState(false);
  // Two more day filters, mirroring the Issues page: activities RAISED
  // today, and activities DUE today (ending today, or overdue and not
  // finished). Independent of the running-today filter above.
  const [createdToday, setCreatedToday] = useState(false);
  const [dueToday, setDueToday] = useState(false);
  // The windowed form of those two: activities STARTING in a range, and
  // activities FINISHING in one. Uncontrolled like the buttons beside
  // them, so every surface that embeds this chart - schedule view,
  // dashboard, the Scheduler's draft - gets them without being changed.
  const [startRange, setStartRange] = useState({ from: "", to: "" });
  const [endRange, setEndRange] = useState({ from: "", to: "" });
  // Signed off by owner/GC: "signed,planned,not", any of them (owner,
  // 2026-10-21) - the issue and shop drawing filter, on the schedule.
  const [ownerFilter, setOwnerFilter] = useState("");
  const ownerStates = ownerFilter ? ownerFilter.split(",").filter(Boolean) : [];
  const ownerKey = ownerStates.join(",");
  const todayFilter = todayOnly !== undefined ? !!todayOnly : ownToday;
  // Collapsed subtrees: item ids (plan structure) and lane keys
  // (contractor grouping) whose children are hidden. Toggled by the
  // triangle in front of the Activity cell; remembered per person and per
  // project (only where the chart is given its project).
  const [savedLanes, setSavedLanes] = usePreference("gantt.collapsed", [], { projectId: projectId || "", legacyKey: projectId ? `gantt_collapsed_${projectId}` : "" });
  const [localLanes, setLocalLanes] = useState([]);
  const lanes = projectId ? savedLanes : localLanes;
  const collapsed = useMemo(() => new Set((Array.isArray(lanes) ? lanes : []).map(String)), [lanes]);
  const setCollapsed = (fn) => {
    const next = [...(typeof fn === "function" ? fn(new Set(collapsed)) : fn)];
    if (projectId) setSavedLanes(next); else setLocalLanes(next);
  };
  const toggleCollapsed = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  // Time-scale zoom (px per day). Ctrl+wheel over the chart, or the −/+
  // buttons; remembered per person.
  const [zoomPref, setPxPerDay] = usePreference("gantt.zoom", DEFAULT_PX_PER_DAY, { legacyKey: "gantt_px_per_day" });
  const pxPerDay = Number(zoomPref) >= 4 && Number(zoomPref) <= 120 ? Number(zoomPref) : DEFAULT_PX_PER_DAY;
  const PX_PER_DAY = pxPerDay;
  // Resizable left columns (drag the header edge); widths remembered.
  const [savedWidths, setColWidths] = usePreference("gantt.colWidths", null, { legacyKey: "gantt_col_widths" });
  const colWidths = useMemo(() => ({ ...DEFAULT_COL_WIDTHS, ...(savedWidths && typeof savedWidths === "object" ? savedWidths : {}) }), [savedWidths]);
  const colResizeRef = useRef(null); // {key, startX, startW}
  const beginColResize = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    colResizeRef.current = { key, startX: e.clientX, startW: colWidths[key] };
    const onMove = (ev) => {
      const r = colResizeRef.current; if (!r) return;
      const w = Math.max(48, Math.min(700, r.startW + (ev.clientX - r.startX)));
      setColWidths((c) => ({ ...c, [r.key]: w }));
    };
    const onUp = () => { colResizeRef.current = null; window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  // `editable` may be a BOOLEAN (owner-side: the whole plan) or a
  // PREDICATE (a partner with allowPlanEdit: only the rows their company
  // is responsible for). One shape would not do - the owner edits
  // everything and the partner edits a subset of the same chart, drawn
  // side by side.
  const canDrag = (it) => (typeof editable === "function" ? !!editable(it) : !!editable);
  const editableAny = typeof editable === "function" ? true : !!editable;
  const canToggleDisabled = !!onToggleDisabled || (editableAny && !!projectId && !onLocalChange);
  const leftCols = [...(canToggleDisabled ? [DISABLE_COL] : []), ...LEFT_COLS, ...(onEditItem ? [EDIT_COL] : [])];
  const widthOf = (c) => (c.fixed ? (c.w || EDIT_COL_W) : (colWidths[c.key] || 0));
  const toggleDisabled = async (item, next) => {
    if (onToggleDisabled) return onToggleDisabled(item, next);
    const r = await patchItem(String(item._id), { disabled: next }, next ? `"${item.name}" disabled - its issues and checklist items are abandoned` : `"${item.name}" enabled again`);
    return r;
  };
  const leftW = leftCols.reduce((a, c) => a + widthOf(c), 0);
  // Hover card for bars / rows / markers: {x, y, kind:'item'|'issue'|'checklist', item|data}
  // x/y are container-relative (see onHoverMove) so the card follows the
  // mouse correctly under any page zoom.
  const [hover, setHover] = useState(null);
  const scrollRef = useRef(null);
  const rootRef = useRef(null);
  // WHOLE-WINDOW VIEW (owner, 2026-10-12: "add a full screen to the
  // interactive gantt chart for easy navigation"). The chart is pinned over
  // the page rather than handed to the browser's Fullscreen API: an element
  // in browser full screen hides everything rendered outside it, and the
  // date pickers, tooltips and the Schedule page's dialogs are all rendered
  // at the end of <body>. Esc or the button brings it back.
  const [fullScreen, setFullScreen] = useState(false);
  useEffect(() => {
    if (!fullScreen) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !e.defaultPrevented) setFullScreen(false); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [fullScreen]);
  useEffect(() => {
    let raf = 0;
    // The top edge the header should stay under: the nearest ancestor that
    // actually scrolls, or the window.
    // ...and below anything pinned over that edge: the app's fixed top bar
    // covered the header on the Coordination page until this looked for it.
    // Only what lies ABOVE this chart counts - stop at the first element of
    // our own (in full screen the app bar sits under the overlay).
    const boundary = (grid) => {
      let top = 0;
      for (let el = grid.parentElement; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
        const oy = window.getComputedStyle(el).overflowY;
        if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight) { top = Math.max(0, el.getBoundingClientRect().top); break; }
      }
      const root = rootRef.current;
      if (root && typeof document.elementsFromPoint === "function") {
        const g = grid.getBoundingClientRect();
        for (const el of document.elementsFromPoint(g.left + g.width / 2, top + 2)) {
          if (root.contains(el)) break;
          for (let a = el; a && a !== document.body; a = a.parentElement) {
            const pos = window.getComputedStyle(a).position;
            if (pos === "fixed" || pos === "sticky") { const b = a.getBoundingClientRect(); if (b.top <= top + 2 && b.bottom > top) top = b.bottom; break; }
          }
        }
      }
      return top;
    };
    const apply = () => {
      raf = 0;
      const grid = gridRef.current;
      if (!grid) return;
      const r = grid.getBoundingClientRect();
      // Page zoom: rects are visual px, the transform is layout px.
      const ratio = grid.offsetHeight ? r.height / grid.offsetHeight : 1;
      const room = Math.max(0, grid.offsetHeight - HEADER_H - ROW_H);
      // Less the chart's own top border, so no sliver of rows shows above it.
      const off = Math.min(room, Math.max(0, (boundary(grid) - r.top) / (ratio || 1) - (grid.clientTop || 0)));
      const t = off > 0 ? `translateY(${off}px)` : "";
      for (const ref of [leftHeadRef, rightHeadRef]) if (ref.current) ref.current.style.transform = t;
    };
    const onScroll = () => { if (!raf) raf = window.requestAnimationFrame(apply); };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    apply();
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  });
  // STICKY HEADER (owner, 2026-10-12: "make the header sticky so the dates
  // and the labels stay visible as we scroll"). CSS sticky cannot do it: the
  // dates are drawn inside the chart's SVG, and both panes clip overflow,
  // which makes each its own (non-scrolling) sticky container. So the left
  // header row and a copy of the date strip are MOVED with the scroll: one
  // transform written straight to the two elements per animation frame, never
  // a React state change - re-rendering this chart on every scroll event is
  // what would make the page crawl. Works for whatever scrolls the page: the
  // window, an app scroller, or the full-screen overlay.
  const gridRef = useRef(null);
  const leftHeadRef = useRef(null);
  const rightHeadRef = useRef(null);
  const rootStyle = fullScreen
    ? { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, background: "#fff", padding: 12, overflow: "auto" }
    : { position: "relative" };
  const svgRef = useRef(null);
  // Pointer position in the root wrapper's LAYOUT pixels - independent of
  // page zoom (html { zoom }), which breaks any clientX-vs-getBounding
  // ClientRect mix. Chart hits: SVG screen CTM (spec-consistent with
  // clientX) -> user units, offset by the svg's layout position minus the
  // horizontal scroll. Table hits: offsetX/Y (target-local CSS px) plus
  // the target's layout offset within the root.
  const offsetWithin = (el, root) => {
    let x = 0, y = 0, n = el;
    while (n && n !== root && n.offsetParent !== undefined) {
      x += n.offsetLeft || 0; y += n.offsetTop || 0;
      n = n.offsetParent;
      if (n === root) break;
    }
    return { x, y };
  };
  const toLocal = (e) => {
    const root = rootRef.current;
    const svg = svgRef.current;
    const w = root ? root.offsetWidth : 800;
    // The hover card is clamped to the root's box (see HoverCard), so the
    // height travels with every hover point the same way the width does.
    const rootH = root ? root.offsetHeight : 0;
    try {
      // Chart hits: SVG screen CTM maps client -> user units and includes
      // CSS zoom in every Chrome; the svg sits at the scroll box's origin.
      if (root && svg && e.target && svg.contains(e.target)) {
        const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
        const m = svg.getScreenCTM();
        if (m) {
          const p = pt.matrixTransform(m.inverse());
          const box = scrollRef.current;
          const off = box ? offsetWithin(box, root) : { x: 0, y: 0 };
          return { x: off.x + p.x - (box ? box.scrollLeft : 0), y: off.y + p.y - (box ? box.scrollTop : 0), w, rootH };
        }
      }
      // Table hits: convert client px -> the target's layout px. Chrome
      // 128+ (standard zoom): rects are visual px like clientX -> divide
      // by the visual/layout ratio. Older Chrome: rects are layout px
      // while clientX is visual -> unzoom clientX with the root's
      // computed zoom first.
      if (root && e.target && e.target.getBoundingClientRect) {
        const t = e.target;
        const r = t.getBoundingClientRect();
        const rr = root.getBoundingClientRect();
        const ratio = root.offsetWidth ? rr.width / root.offsetWidth : 1; // <1 when rects are visual px
        let zoom = 1;
        try { zoom = parseFloat(getComputedStyle(document.documentElement).zoom) || 1; } catch { zoom = 1; }
        let lx, ly;
        if (ratio < 0.995 || ratio > 1.005) { lx = (e.clientX - r.left) / ratio; ly = (e.clientY - r.top) / ratio; }
        else { lx = e.clientX / zoom - r.left; ly = e.clientY / zoom - r.top; }
        const off = offsetWithin(t, root);
        return { x: off.x + lx, y: off.y + ly, w, rootH };
      }
    } catch { /* fall through */ }
    return { x: 0, y: 0, w, rootH };
  };
  // NOTE: read the event synchronously - React 16 pools synthetic events,
  // so touching e.target/clientX inside a state-updater callback (which
  // runs later) sees a recycled, empty event and mapped to (0,0).
  const hoverOn = (kind, payload) => (e) => { const p = toLocal(e); setHover({ ...p, kind, ...payload }); };
  const hoverMove = (e) => { const p = toLocal(e); setHover((h) => (h ? { ...h, ...p } : h)); };
  const hoverOff = () => setHover(null);
  // Contractor names + lane colours from the roster (when the parent
  // didn't hand them in), so tooltips can show who owns a task.
  const [rosterMeta, setRosterMeta] = useState({ names: {}, colors: {}, trades: {} });
  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { url, tokenRef } = require("api");
        const resp = await fetch(`${url}/projects/${projectId}/participants/roster`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } });
        const body = await resp.json();
        if (cancelled || !resp.ok || !Array.isArray(body)) return;
        const names = {}, colors = {}, trades = {};
        for (const g of body) {
          if (!g.companyId) continue;
          names[g.companyId] = g.companyName || "Company";
          if (g.color) colors[g.companyId] = g.color;
          if (Array.isArray(g.trades) && g.trades.length) trades[g.companyId] = g.trades.join(" ");
        }
        setRosterMeta({ names, colors, trades, ix: partnerTradeIndex(body) });
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [projectId]);
  // Labels for the two grouping axes that store KEYS rather than words:
  // an area id, and a project-stage key from the company's taxonomy.
  // Both fail quiet - a lane falls back to the raw key, which is still
  // a usable grouping.
  const [areaNames, setAreaNames] = useState({});
  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { listAreaRows } = require("api/areas/areasApi");
        const rows = await listAreaRows(projectId, "approved");
        if (cancelled) return;
        const names = {};
        for (const a of rows) names[String(a._id)] = `${a.code ? `${a.code} · ` : ""}${a.name}`;
        setAreaNames(names);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [projectId]);
  const [stageLabels, setStageLabels] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getTaxonomy } = require("api/taxonomy/taxonomyApi");
        const tax = await getTaxonomy();
        if (cancelled) return;
        const labels = {};
        for (const r of tax.project_stage || []) labels[r.key] = r.label;
        setStageLabels(labels);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const nameOfCompany = (id) => (companyNames && companyNames[id]) || rosterMeta.names[id] || "";
  const colorOfCompany = (id) => rosterMeta.colors[id] || "";
  // Optimistic date/progress patches, keyed by item id, cleared as soon
  // as the parent hands us a fresh items array.
  const [overrides, setOverrides] = useState({});
  const [drag, setDrag] = useState(null); // {gid, mode:'move'|'resize'|'start', deltaDays}
  // Right-click menu {x, y, item, date} and the row-drop indicator
  // {key, where}; the dragged row's id lives in a ref (no re-render per move).
  const [ctxMenu, setCtxMenu] = useState(null);
  const [dropAt, setDropAt] = useState(null);
  const dragRowRef = useRef(null);
  const [progressEditor, setProgressEditor] = useState(null); // {gid,name,value,x,y}
  const [savingGid, setSavingGid] = useState(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    setOverrides({});
  }, [items]);

  const today = startOfDay(new Date());
  // Window covers the WHOLE plan (earliest start − 1 week … latest end +
  // 2 weeks) and always at least 2 weeks back / 4 weeks ahead of today,
  // so long schedules scroll left↔right instead of being clipped.
  const { windowStart, windowEnd } = useMemo(() => {
    let minT = today.getTime() - 14 * DAY_MS;
    let maxT = today.getTime() + 28 * DAY_MS;
    for (const it of items || []) {
      const s0 = startOfDay(new Date(it.startDate)).getTime();
      const e0 = startOfDay(new Date(it.endDate || it.startDate)).getTime();
      if (!isNaN(s0)) minT = Math.min(minT, s0 - 7 * DAY_MS);
      if (!isNaN(e0)) maxT = Math.max(maxT, e0 + 14 * DAY_MS);
    }
    // safety cap: 3 years
    if (maxT - minT > 3 * 365 * DAY_MS) maxT = minT + 3 * 365 * DAY_MS;
    return { windowStart: new Date(minT), windowEnd: new Date(maxT) };
  }, [items]); // eslint-disable-line
  const totalDays = dayDiff(windowEnd, windowStart) + 1; // inclusive
  const chartW = totalDays * PX_PER_DAY;

  // Ctrl+wheel = zoom the time scale around the cursor; the container is
  // a normal horizontal scroller otherwise (Shift+wheel / trackpad).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mouseX = e.clientX - rect.left + el.scrollLeft; // chart px under cursor
      const dayUnder = mouseX / pxPerDay;
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const next = Math.max(4, Math.min(120, Math.round(pxPerDay * factor * 10) / 10));
      if (next === pxPerDay) return;
      setPxPerDay(next);
      requestAnimationFrame(() => { el.scrollLeft = Math.max(0, dayUnder * next - (e.clientX - rect.left)); });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [pxPerDay]);

  // First paint: scroll so "today" sits near the left third.
  const didInitialScroll = useRef(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || didInitialScroll.current || !(items || []).length) return;
    didInitialScroll.current = true;
    const x = dayDiff(today, windowStart) * pxPerDay - el.clientWidth / 3;
    el.scrollLeft = Math.max(0, x);
  }, [items, windowStart, pxPerDay]); // eslint-disable-line

  const mergedItems = useMemo(
    () =>
      (items || []).map((it) =>
        overrides[String(it._id)] ? { ...it, ...overrides[String(it._id)] } : it
      ),
    [items, overrides]
  );
  const itemById = useMemo(
    () => new Map(mergedItems.map((i) => [String(i._id), i])),
    [mergedItems]
  );
  // Rows whose % is the average of their tasks (never editable).
  const parentIds = useMemo(() => parentIdsOf(items), [items]);
  const mayEditProgress = (it) => !!projectId && !onLocalChange && !it.redacted && !parentIds.has(String(it._id))
    && (typeof canEditProgress === "function" ? !!canEditProgress(it) : !!canEditProgress);
  // Save a leaf's %, then repaint it and every parent above it from the
  // server's answer - no reload needed.
  const saveLeafProgress = async (it, value) => {
    const gid = String(it._id);
    try {
      setSavingGid(gid);
      const r = await setGanttItemProgress(projectId, gid, value);
      const next = {};
      next[gid] = { progress: r && r.item ? r.item.progress : value, ...(r && r.item && r.item.status ? { status: r.item.status } : {}) };
      for (const a of (r && r.ancestors) || []) next[String(a._id)] = { progress: a.progress };
      setOverrides((o) => {
        const out = { ...o };
        for (const [k, v] of Object.entries(next)) out[k] = { ...(o[k] || {}), ...v };
        return out;
      });
      message.success(`Progress set to ${pctText(value)}`);
      if (onChanged && r && r.item) onChanged(r.item);
    } catch (err) {
      message.error(err?.message || "Progress update failed");
    } finally {
      setSavingGid(null);
    }
  };

  // Lane labels: caller-supplied names first, then the roster names the
  // feed already carries (so lanes never show raw ids).
  const laneNames = useMemo(() => ({ ...(rosterMeta.names || {}), ...(companyNames || {}) }), [rosterMeta, companyNames]);
  // The second line of a lane header: a contractor's trades, a discipline's
  // partners (components/Partners/partnerTrades).
  const laneSubline = (key) => {
    const ix = rosterMeta.ix;
    if (!ix || !key) return "";
    if (groupMode === "company" && key.startsWith("lane-")) return tradesText(ix.tradesOf(key.slice(5)));
    if (groupMode === "discipline" && key.startsWith("discipline-")) return partnersText(ix.partnersOf(key.slice(11)));
    return "";
  };
  // Search filter: matching rows plus their ancestors (tree context) and
  // their children (a matching summary shows what is under it).
  const visibleItems = useMemo(() => {
    const dated = !rangeEmpty(startRange) || !rangeEmpty(endRange);
    if (!query && !todayFilter && !createdToday && !dueToday && !dated && !ownerKey) return mergedItems;
    const byId = new Map(mergedItems.map((i) => [String(i._id), i]));
    const kids = new Map();
    for (const it of mergedItems) {
      const pid = it.parentId ? String(it.parentId) : "";
      if (pid && byId.has(pid)) { if (!kids.has(pid)) kids.set(pid, []); kids.get(pid).push(String(it._id)); }
    }
    // Matches name / WBS / type / status / contractor (company name, its
    // trades, the document label) / person / discipline / area / source id.
    const hay = (it) => [
      it.wbs, it.name, TYPE_LABEL[it.type] || it.type, STATUS_LABEL[it.status] || it.status,
      laneNames[String(it.responsibleCompanyId || "")], (rosterMeta.trades || {})[String(it.responsibleCompanyId || "")], it.contractorLabel,
      it.responsibleUserName, ...((it.responsibleUsers || []).map((u) => `${u.name || ""} ${u.email || ""}`)), it.discipline, it.areaKey, it.level, it.grid, it.externalRef,
    ].filter(Boolean).join(" ").toLowerCase();
    const t0 = todayKey();
    const keep = new Set();
    for (const it of mergedItems) {
      if (query && !hay(it).includes(query)) continue;
      if (todayFilter && !runsToday(it, t0)) continue;
      if (createdToday && !madeToday(it, t0)) continue;
      if (dueToday && !dueTodayItem(it, t0)) continue;
      if (ownerKey && !ownerKey.split(",").includes(ownerStateOfItem(it))) continue;
      // Start / finish windows. utcDayKey normalises both shapes these
      // items arrive in - a stored ISO instant at midnight UTC, and the
      // plain 'YYYY-MM-DD' the Scheduler's draft rows carry.
      if (!inDayRange(utcDayKey(it.startDate), startRange.from, startRange.to)) continue;
      if (!inDayRange(utcDayKey(it.endDate || it.startDate), endRange.from, endRange.to)) continue;
      const id = String(it._id);
      keep.add(id);
      let p = it.parentId ? String(it.parentId) : "";
      while (p && byId.has(p) && !keep.has(p)) { keep.add(p); const pi = byId.get(p); p = pi.parentId ? String(pi.parentId) : ""; }
      const stack = [...(kids.get(id) || [])];
      while (stack.length) { const c = stack.pop(); if (keep.has(c)) continue; keep.add(c); stack.push(...(kids.get(c) || [])); }
    }
    return mergedItems.filter((i) => keep.has(String(i._id)));
  }, [mergedItems, query, todayFilter, createdToday, dueToday, startRange.from, startRange.to, endRange.from, endRange.to, laneNames, rosterMeta, ownerKey]);
  // The lane an activity belongs to, per grouping. `area` reads the
  // area REFERENCE first and falls back to the free text, so a project
  // that has not drawn its areas yet still groups by what it typed.
  const groupOf = useMemo(() => ({
    area: {
      keyOf: (it) => (it.areaId ? `id:${String(it.areaId)}` : (it.areaKey ? `txt:${it.areaKey}` : "")),
      labelOf: (k, its) => (k.startsWith("id:")
        ? (areaNames[k.slice(3)] || its[0]?.areaKey || "Area")
        : k.slice(4)),
    },
    discipline: { keyOf: (it) => it.discipline || "", labelOf: (k) => k },
    stage: { keyOf: (it) => it.stage || "", labelOf: (k) => stageLabels[k] || k },
    // BIM-to-Field: one work package = the BIM chain + the Field chain of a scope.
    package: { keyOf: (it) => it.workPackage || "", labelOf: (k) => k, empty: "—" },
    phase: { keyOf: (it) => it.phase || "", labelOf: (k) => PHASE_LABEL[k] || k, empty: "Unclassified" },
  }), [areaNames, stageLabels]);

  const rows = useMemo(
    () => {
      if (groupMode === "company") return buildCompanyRows(visibleItems, laneNames, collapsed);
      const g = groupOf[groupMode];
      if (g) return buildGroupedRows(visibleItems, g.keyOf, g.labelOf, collapsed, groupMode, g.empty);
      return flattenTree(visibleItems, collapsed);
    },
    [visibleItems, groupMode, laneNames, collapsed, groupOf]
  );
  // Every collapsible key in the current grouping (for Collapse all /
  // Expand all): parents with children, or contractor lane keys.
  const collapsibleKeys = useMemo(() => {
    if (groupMode === "company") {
      return buildCompanyRows(mergedItems, laneNames).filter((r) => r.group).map((r) => r.key);
    }
    if (groupOf[groupMode]) {
      const g = groupOf[groupMode];
      return buildGroupedRows(mergedItems, g.keyOf, g.labelOf, null, groupMode, g.empty)
        .filter((r) => r.group).map((r) => r.key);
    }
    const parents = new Set(mergedItems.map((i) => (i.parentId ? String(i.parentId) : "")).filter(Boolean));
    return mergedItems.map((i) => String(i._id)).filter((id) => parents.has(id));
  }, [mergedItems, groupMode, laneNames]);
  const anyCollapsed = collapsibleKeys.some((k) => collapsed.has(k));
  const allCollapsed = collapsibleKeys.length > 0 && collapsibleKeys.every((k) => collapsed.has(k));
  const chartH = HEADER_H + rows.length * ROW_H;

  const xOf = (date) => dayDiff(date, windowStart) * PX_PER_DAY;

  // Item dates with the live drag preview applied.
  const effDates = (item) => {
    let start = startOfDay(new Date(item.startDate));
    let end = startOfDay(new Date(item.endDate || item.startDate));
    if (drag && drag.gid === String(item._id) && drag.deltaDays) {
      if (drag.mode === "move") {
        start = shiftDays(start, drag.deltaDays);
        end = shiftDays(end, drag.deltaDays);
      } else if (drag.mode === "start") {
        start = shiftDays(start, drag.deltaDays);
        if (start > end) start = end;
      } else {
        end = shiftDays(end, drag.deltaDays);
        if (end < start) end = start;
      }
    }
    return { start, end };
  };

  // Per-item geometry (bar extents + row centerline) for this render;
  // dependency arrows and the marks share it.
  const geomById = {};
  rows.forEach((row, idx) => {
    if (!row.item) return;
    const { start, end } = effDates(row.item);
    const cy = HEADER_H + idx * ROW_H + ROW_H / 2;
    const inWindow = end >= windowStart && start <= windowEnd;
    const clampedStart = start > windowStart ? start : windowStart;
    const clampedEnd = end < windowEnd ? end : windowEnd;
    let x1 = xOf(clampedStart);
    let x2 = xOf(clampedEnd) + PX_PER_DAY; // end day inclusive
    const t = row.item.type;
    if (isMilestone(t)) {
      const cx = xOf(end) + PX_PER_DAY / 2;
      x1 = cx - 6;
      x2 = cx + 6;
    }
    geomById[String(row.item._id)] = { x1, x2, cy, inWindow, start, end };
  });

  // ======================= PATCH plumbing ==================================

  const patchItem = async (gid, body, okMsg) => {
    if (onLocalChange && (body.startDate !== undefined || body.endDate !== undefined)) {
      onLocalChange(gid, body);
      return { _id: gid, ...body };
    }
    if (!projectId) return null;
    try {
      setSavingGid(gid);
      const updated = await updateGanttItem(projectId, gid, body);
      setOverrides((o) => ({
        ...o,
        [gid]: {
          ...(o[gid] || {}),
          ...(body.startDate !== undefined ? { startDate: updated.startDate } : {}),
          ...(body.endDate !== undefined ? { endDate: updated.endDate } : {}),
          ...(body.progress !== undefined ? { progress: updated.progress } : {}),
        },
      }));
      if (okMsg) message.success(okMsg);
      if (onChanged) onChanged(updated);
      return updated;
    } catch (err) {
      message.error(err?.message || "Activity update failed");
      return null;
    } finally {
      setSavingGid(null);
    }
  };

  // ======================= mouse panning ===================================
  // Press on the chart background (or anything that did not start a bar
  // drag) and move sideways to scroll the time axis. A pan that actually
  // moved suppresses the click that would otherwise open the row.
  const [panning, setPanning] = useState(false);
  const beginPan = (e) => {
    if (e.button !== 0) return;
    const box = scrollRef.current;
    const svg = svgRef.current;
    // Only presses on the chart itself pan - not the progress popover
    // (its slider needs the mousedown) or anything else laid over it.
    if (!box || !svg || !(e.target === svg || svg.contains(e.target))) return;
    if (box.scrollWidth <= box.clientWidth + 1) return;
    e.preventDefault();
    const startX = e.clientX;
    const startLeft = box.scrollLeft;
    // clientX is in visual px, scrollLeft in layout px - they differ under
    // page zoom (Chrome 128+ rects are visual px, older builds layout px).
    const rectW = box.getBoundingClientRect().width || box.offsetWidth;
    const ratio = box.offsetWidth ? rectW / box.offsetWidth : 1;
    let moved = false;
    setPanning(true);
    const onMove = (ev) => {
      const dx = (ev.clientX - startX) / (ratio || 1);
      if (Math.abs(dx) > 3) moved = true;
      box.scrollLeft = startLeft - dx;
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setPanning(false);
      if (moved) {
        suppressClickRef.current = true;
        setTimeout(() => { suppressClickRef.current = false; }, 0);
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ======================= right-click menu (draft) =========================
  // The day under the pointer, from the svg's own coordinates so page zoom
  // and horizontal scroll cannot shift it.
  const dateAtEvent = (e) => {
    const svg = svgRef.current;
    if (!svg || !e.target || !svg.contains(e.target)) return null;
    try {
      const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
      const m = svg.getScreenCTM();
      if (!m) return null;
      const p = pt.matrixTransform(m.inverse());
      return toIsoDate(shiftDays(windowStart, Math.floor(p.x / PX_PER_DAY)));
    } catch { return null; }
  };
  const openContext = (e, item) => {
    if (!onContextAction || !item || item.redacted || !canDrag(item)) return;
    e.preventDefault();
    e.stopPropagation();
    const at = toLocal(e);
    setHover(null);
    setCtxMenu({ x: at.x, y: at.y, w: at.w, item, date: dateAtEvent(e),
      progressText: (() => { const isP = parentIds.has(String(item._id)); const d = pctDisplay(item, isP); return d.unknown ? "no data entered" : `${d.text}${isP ? ` · ${d.tip.toLowerCase()}` : " complete"}`; })() });
  };

  // ======================= row drag-and-drop (draft) =======================
  // Only in the WBS view: a company / area grouping is not the plan's
  // structure, so dropping there would mean nothing.
  const dropAtRef = useRef(null);
  dropAtRef.current = dropAt;
  const rowDnD = (item) => {
    if (!onMoveRow || groupMode !== "wbs" || !item || item.redacted || !canDrag(item)) return {};
    return rowDragHandlers(String(item._id), {
      drag: { get: () => dragRowRef.current, set: (v) => { dragRowRef.current = v; } },
      drop: { get: () => dropAtRef.current, set: (v) => { dropAtRef.current = v; setDropAt(v); } },
      onMoveRow,
    });
  };

  // ======================= drag-to-reschedule ==============================

  const beginDrag = (e, item, mode) => {
    if (!canDrag(item) || !(projectId || onLocalChange) || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const gid = String(item._id);
    const startClientX = e.clientX;
    const origStart = startOfDay(new Date(item.startDate));
    const origEnd = startOfDay(new Date(item.endDate || item.startDate));
    setDrag({ gid, mode, deltaDays: 0 });

    const daysAt = (clientX) => Math.round((clientX - startClientX) / PX_PER_DAY);
    const onMove = (ev) => {
      const dd = daysAt(ev.clientX);
      setDrag((d) => (d && d.gid === gid && d.deltaDays !== dd ? { ...d, deltaDays: dd } : d));
    };
    const onUp = (ev) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDrag(null);
      if (Math.abs(ev.clientX - startClientX) > 3) {
        // The click event (if any) fires synchronously after mouseup;
        // clear the flag right after so it can't swallow a later click.
        suppressClickRef.current = true;
        setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
      }
      const dd = daysAt(ev.clientX);
      if (!dd) return;
      let newEnd = shiftDays(origEnd, dd);
      if (mode === "move") {
        const newStart = shiftDays(origStart, dd);
        patchItem(gid, { startDate: toIsoDate(newStart), endDate: toIsoDate(newEnd) });
      } else if (mode === "start") {
        // Left edge: the start moves, the finish stays - the bar expands
        // or retracts from the front, exactly like editing the start.
        let newStart = shiftDays(origStart, dd);
        if (newStart > origEnd) newStart = origEnd;
        patchItem(gid, { startDate: toIsoDate(newStart), endDate: toIsoDate(origEnd) });
      } else {
        if (newEnd < origStart) newEnd = origStart;
        patchItem(gid, { endDate: toIsoDate(newEnd) });
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ======================= contractor progress =============================

  const openProgressEditor = (item, x, cy) => {
    setProgressEditor({
      gid: String(item._id),
      name: item.name,
      value: Number(item.progress) || 0,
      x: Math.max(0, Math.min(x, chartW - 250)),
      y: cy + 12,
    });
  };

  const saveProgress = async () => {
    const pe = progressEditor;
    if (!pe) return;
    const updated = await patchItem(pe.gid, { progress: pe.value }, null);
    if (updated && updated.pendingReview) message.success(`Reported ${pe.value}% - sent to the project owner's Review Center; the schedule updates once it is approved`);
    else if (updated) message.success("Progress updated");
    if (updated) setProgressEditor(null);
  };

  // ======================= dependency arrows ===============================

  const arrows = [];
  rows.forEach((row) => {
    const it = row.item;
    if (!it || !Array.isArray(it.dependencies) || !it.dependencies.length) return;
    const succ = geomById[String(it._id)];
    if (!succ || !succ.inWindow) return;
    for (const dep of it.dependencies) {
      const pid = depId(dep);
      const pred = geomById[pid];
      const predItem = itemById.get(pid);
      if (!pred || !predItem || !pred.inWindow || pred.cy === succ.cy) continue;
      const behind = predItem.healthStatus === "behind" && predItem.status !== "done";
      const color = behind ? "#d4380d" : "#8c8c8c";
      // The relationship decides which END of each bar the arrow touches:
      // an SS tie drawn from the predecessor's finish would be drawing a
      // constraint that is not there.
      const type = depType(dep);
      const y1 = pred.cy, y2 = succ.cy;
      const x1 = type === "SS" || type === "SF" ? pred.x1 : pred.x2;
      const x2 = type === "FF" || type === "SF" ? succ.x2 : succ.x1;
      const lag = depLag(dep);
      arrows.push(
        <g key={`dep-${pid}-${type}-${it._id}`} pointerEvents="none">
          <path
            d={depArrowPath(x1, y1, x2, y2, ROW_H)}
            fill="none"
            stroke={color}
            strokeWidth={behind ? 1.6 : 1.2}
            opacity={behind ? 0.95 : 0.6}
          />
          <polygon
            points={type === "FF" || type === "SF"
              ? `${x2},${y2} ${x2 + 6},${y2 - 3.5} ${x2 + 6},${y2 + 3.5}`
              : `${x2},${y2} ${x2 - 6},${y2 - 3.5} ${x2 - 6},${y2 + 3.5}`}
            fill={color}
            opacity={behind ? 0.95 : 0.6}
          />
          {/* Anything other than a plain finish-to-start is labelled -
              the shape of the elbow cannot say "SS" or "+3d" on its own. */}
          {(type !== "FS" || lag !== 0) && (
            <text
              x={(x1 + x2) / 2}
              y={(y1 + y2) / 2 - 3}
              fontSize={9}
              fill={color}
              textAnchor="middle"
              opacity={0.95}
            >
              {`${type}${lag ? (lag > 0 ? `+${lag}d` : `${lag}d`) : ""}`}
            </text>
          )}
          {behind && (
            <text
              x={x1 + 11}
              y={(y1 + y2) / 2 + 4}
              fontSize={11}
              fill="#d4380d"
              fontWeight={700}
            >
              ⚠
            </text>
          )}
          <title>
            {`${predItem.name} → ${it.name}${behind ? " — blocked: predecessor is behind" : ""}`}
          </title>
        </g>
      );
    }
  });

  // ======================= render ==========================================

  // WEEKEND BANDS, zoomed in only (owner, 2026-09-30: "only add the weekend
  // barring when you zoom in"). Display only - no data, no engine, no stored
  // date changes: this draws behind the bars and nothing reads it.
  const weekendBands = PX_PER_DAY >= WEEKEND_BAND_MIN_PX_PER_DAY
    ? weekendDays(windowStart, windowEnd)
    : [];

  // Week gridlines: every Monday in the window
  const weekLines = [];
  for (let t = windowStart.getTime(); t <= windowEnd.getTime(); t += DAY_MS) {
    const d = new Date(t);
    if (d.getDay() === 1) weekLines.push(d);
  }

  const searchBox = filterText === undefined ? (
    <Input.Search
      allowClear size="small" placeholder="Search activities, contractors, trades…" value={ownFilter}
      onChange={(e) => setOwnFilter(e.target.value)} style={{ width: 200 }}
    />
  ) : null;

  // A filter that matches nothing keeps the toolbar (so it can be turned
  // off) and says which filter; only an empty schedule gets this screen.
  const noRowsMessage = emptyMessage({ total: mergedItems.length, shown: rows.length, query, todayFilter, createdToday, dueToday, dated: !rangeEmpty(startRange) || !rangeEmpty(endRange), owner: ownerKey });
  if (!rows.length && !mergedItems.length) {
    return (
      <div ref={rootRef} style={{ position: "relative" }}>
        {(searchBox || todayOnly === undefined) && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 8 }}>
            {searchBox}
            {todayOnly === undefined && <Button size="small" type={ownToday ? "primary" : "default"} onClick={() => setOwnToday((v) => !v)}>Today</Button>}
          </div>
        )}
        <div style={{ color: "#888", padding: 24, textAlign: "center" }}>
          {noRowsMessage}
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} style={rootStyle} data-testid="gantt-root" data-fullscreen={fullScreen ? "1" : "0"}>
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        {searchBox}
        {todayOnly === undefined && (
          <Button size="small" type={ownToday ? "primary" : "default"} title="Only activities running today (or overdue and not done); also scrolls the chart to today"
            onClick={() => { setOwnToday((v) => !v); const el = scrollRef.current; if (el) el.scrollLeft = Math.max(0, dayDiff(today, windowStart) * pxPerDay - el.clientWidth / 3); }}>
            Today
          </Button>
        )}
        <Button size="small" type={createdToday ? "primary" : "default"} title="Activities added today"
          onClick={() => setCreatedToday((v) => !v)}>
          Created today
        </Button>
        <Button size="small" type={dueToday ? "primary" : "default"} title="Activities ending today, or overdue and not done"
          onClick={() => { setDueToday((v) => !v); const el = scrollRef.current; if (el) el.scrollLeft = Math.max(0, dayDiff(today, windowStart) * pxPerDay - el.clientWidth / 3); }}>
          Due today
        </Button>
        <DateRangeFilter size="small" label="Starts" value={startRange} onChange={setStartRange}
          title="Activities starting between these two days, inclusive. Leave either end empty for an open window." />
        <DateRangeFilter size="small" label="Finishes" value={endRange} onChange={setEndRange}
          title="Activities finishing between these two days, inclusive." />
        <OwnerSignOffFilter size="small" width={190} value={ownerFilter} onChange={setOwnerFilter} />
        {(query || todayFilter || createdToday || dueToday || ownerKey || !rangeEmpty(startRange) || !rangeEmpty(endRange)) && <span style={{ fontSize: 11, color: "#666" }}>{visibleItems.length} of {mergedItems.length} activities</span>}
        <span style={{ fontSize: 11, color: "#999" }}>Ctrl + wheel to zoom · drag column edges to resize · hover a bar for details</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 2, border: "1px solid #f0f0f0", borderRadius: 6, padding: "1px 4px" }}>
          <Button size="small" type="text" onClick={() => setPxPerDay((v) => Math.max(4, Math.round(v / 1.25)))} title="Zoom out">−</Button>
          <span style={{ fontSize: 11, minWidth: 44, textAlign: "center", color: "#666" }}>{pxPerDay >= 20 ? "days" : pxPerDay >= 8 ? "weeks" : "months"}</span>
          <Button size="small" type="text" onClick={() => setPxPerDay((v) => Math.min(120, Math.round(v * 1.25)))} title="Zoom in">+</Button>
          <Button size="small" type="text" onClick={() => {
            const el = scrollRef.current; if (!el) return;
            setPxPerDay(Math.max(4, Math.min(120, Math.floor((el.clientWidth - 8) / totalDays * 10) / 10)));
          }} title="Fit the whole plan">Fit</Button>
        </span>
        {collapsibleKeys.length > 0 && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 2, border: "1px solid #f0f0f0", borderRadius: 6, padding: "1px 4px" }}>
            <Button size="small" type="text" disabled={!anyCollapsed} title="Expand every group"
                    onClick={() => setCollapsed((prev) => { const n = new Set(prev); collapsibleKeys.forEach((k) => n.delete(k)); return n; })}>
              Expand all
            </Button>
            <Button size="small" type="text" disabled={allCollapsed} title="Collapse every group"
                    onClick={() => setCollapsed((prev) => { const n = new Set(prev); collapsibleKeys.forEach((k) => n.add(k)); return n; })}>
              Collapse all
            </Button>
          </span>
        )}
        <Button size="small" data-testid="gantt-fullscreen"
                icon={fullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                title={fullScreen ? "Back to the page (Esc)" : "Show the chart over the whole window"}
                onClick={() => setFullScreen((v) => !v)}>
          {fullScreen ? "Exit full screen" : "Full screen"}
        </Button>
        <Segmented
          size="small"
          value={groupMode}
          onChange={setGroupMode}
          options={[
            { label: "By plan structure", value: "wbs" },
            { label: "By contractor", value: "company" },
            { label: "By area", value: "area" },
            { label: "By discipline", value: "discipline" },
            { label: "By stage", value: "stage" },
            { label: "By package", value: "package" },
            { label: "By phase", value: "phase" },
          ]}
        />
      </div>

      {!rows.length && (
        <div data-testid="gantt-filter-empty" style={{ color: "#888", padding: 24, textAlign: "center", border: "1px solid #f0f0f0", borderRadius: 8 }}>
          {noRowsMessage}{" "}
          <Button size="small" type="link" onClick={() => { setOwnFilter(""); setOwnToday(false); setCreatedToday(false); setDueToday(false); setStartRange({ from: "", to: "" }); setEndRange({ from: "", to: "" }); setOwnerFilter(""); }}>
            Clear filters
          </Button>
        </div>
      )}
      <div ref={gridRef} style={{ display: rows.length ? "flex" : "none", border: "1px solid #f0f0f0", borderRadius: 8 }}>
        {/* Fixed left table — resizable columns */}
        <div style={{ width: leftW, minWidth: leftW, borderRight: "1px solid #f0f0f0", overflow: "hidden" }}>
          <div ref={leftHeadRef} data-testid="gantt-sticky-labels" style={{ height: HEADER_H, display: "flex", borderBottom: "1px solid #f0f0f0", background: "#fff", position: "relative", zIndex: 3 }}>
            {leftCols.map((c) => (
              <div key={c.key} style={{
                width: widthOf(c), minWidth: widthOf(c), position: "relative",
                fontSize: 12, fontWeight: 600, color: "#666", display: "flex", alignItems: "center",
                paddingLeft: c.fixed ? 0 : 8, justifyContent: c.fixed ? "center" : undefined,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", boxSizing: "border-box",
              }} title={c.key === "disable" ? "Tick to disable an activity (barred, left out of the emailed schedule, its issues / checklists abandoned)" : c.fixed ? "Edit in the schedule" : undefined}>
                {c.key === "disable" ? <span style={{ fontSize: 10, color: "#bfbfbf" }}>off</span> : c.fixed ? <EditOutlined style={{ color: "#bfbfbf", fontSize: 12 }} /> : c.title}
                {!c.fixed && (
                  <span
                    onMouseDown={(e) => beginColResize(e, c.key)}
                    title="Drag to resize"
                    style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 6, cursor: "col-resize", borderRight: "1px solid #eee" }}
                  />
                )}
              </div>
            ))}
          </div>
          {rows.map(({ item, depth, group, key, hasChildren, collapsed: isCollapsed }) =>
            group ? (
              <div
                key={key}
                title={group}
                onClick={() => toggleCollapsed(key)}
                style={{
                  height: ROW_H, display: "flex", alignItems: "center", paddingLeft: 8, paddingRight: 6,
                  fontSize: 12, fontWeight: 600, color: "#555", background: "#fafafa", cursor: "pointer",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Caret open={!isCollapsed} visible />
                {laneSubline(key) ? (
                  // A contractor lane with its trades beneath, a discipline
                  // lane with its partners beneath (owner, 2026-10-16).
                  <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: "13px", overflow: "hidden" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{group}</span>
                    <span style={{ fontSize: 10, fontWeight: 400, color: "#8c8c8c", overflow: "hidden", textOverflow: "ellipsis" }}>{laneSubline(key)}</span>
                  </span>
                ) : group}
              </div>
            ) : (
              <div
                key={item._id}
                onClick={() => !item.redacted && onSelect && onSelect(item)}
                onMouseEnter={item.redacted ? undefined : hoverOn("item", { item })}
                onMouseMove={item.redacted ? undefined : hoverMove}
                onMouseLeave={hoverOff}
                onContextMenu={onContextAction ? (e) => openContext(e, item) : undefined}
                {...rowDnD(item)}
                data-testid={onMoveRow ? `gantt-row-${item._id}` : undefined}
                title={item.redacted ? "Another trade's activity" : undefined}
                style={{
                  ...(dropAt && dropAt.key === String(item._id)
                    ? (dropAt.where === "above" ? { boxShadow: "inset 0 2px 0 #1677ff" } : { boxShadow: "inset 0 -2px 0 #1677ff" })
                    : {}),
                  height: ROW_H, display: "flex", alignItems: "center", fontSize: 12.5,
                  cursor: onSelect && !item.redacted ? "pointer" : "default", borderBottom: "1px solid #fafafa",
                  color: item.status === "done" || item.disabled ? "#999" : "#333", background: hover?.item === item ? "#f5f9ff" : (item.disabled ? "#fafafa" : "transparent"),
                  textDecoration: item.disabled ? "line-through" : "none", opacity: item.disabled ? 0.75 : 1,
                }}
              >
                {leftCols.map((c) => {
                  if (c.key === "disable") {
                    return (
                      <div key={c.key} style={{ width: c.w, minWidth: c.w, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center" }}
                        onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={!!item.disabled} title={item.disabled ? "Disabled - untick to enable" : "Disable this activity"}
                          style={{ cursor: "pointer", accentColor: "#8c8c8c" }}
                          onChange={(e) => toggleDisabled(item, e.target.checked)} />
                      </div>
                    );
                  }
                  if (c.fixed) {
                    return (
                      <div key={c.key} style={{ width: EDIT_COL_W, minWidth: EDIT_COL_W, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span
                          role="button"
                          aria-label="Edit in schedule"
                          title="Edit this activity in the schedule"
                          className="gantt-row-edit"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); onEditItem(item); }}
                          style={{ fontSize: 12, lineHeight: 1, cursor: "pointer", padding: 4, borderRadius: 4,
                            color: hover?.item === item ? "#1a1a2e" : "#bfbfbf", background: hover?.item === item ? "#e6f0ff" : "transparent" }}
                        >
                          <EditOutlined />
                        </span>
                      </div>
                    );
                  }
                  let text = "";
                  if (item.redacted) text = "";
                  else if (c.key === "name") text = item.name;
                  else if (c.key === "start") text = fmt(startOfDay(new Date(item.startDate)));
                  else if (c.key === "end") text = fmt(startOfDay(new Date(item.endDate || item.startDate)));
                  else if (c.key === "company") {
                    const co = nameOfCompany(item.responsibleCompanyId) || (item.responsibleCompanyId ? "Partner" : "");
                    const people = (item.responsibleUsers && item.responsibleUsers.length ? item.responsibleUsers.map((u) => u.name).filter(Boolean).join(", ") : "") || item.responsibleUserName || "";
                    text = people ? (co ? `${people} - ${co}` : people) : (co || "");
                    // Imported rows name their party by a document label
                    // until it is mapped to a company - show that label.
                    if (!text && item.contractorLabel) text = `${item.contractorLabel} (label)`;
                    if (!text) text = "-";
                  }
                  else if (c.key === "status") {
                    text = STATUS_LABEL[item.status] || item.status || "—";
                    if (item.pendingProgress && item.pendingProgress.at) text += ` · ⏳${Math.round(item.pendingProgress.progress)}%`;
                  }
                  else if (c.key === "updated") text = item.updatedAt ? fmt(new Date(item.updatedAt)) : "—";
                  return (
                    <div key={c.key} title={c.key === "name" ? undefined : (c.key === "updated" && item.updatedAt ? `Last modified ${fmtLong(new Date(item.updatedAt))}${item.lastEditedBy?.name ? ` by ${item.lastEditedBy.name}` : ""}` : text)} style={{
                      width: colWidths[c.key], minWidth: colWidths[c.key], boxSizing: "border-box",
                      paddingLeft: c.key === "name" ? 8 + depth * 14 : 8, paddingRight: 6,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      color: c.key === "name" ? undefined : "#666", fontSize: c.key === "name" ? 12.5 : 11.5,
                      fontWeight: c.key === "name" && hasChildren ? 600 : undefined,
                      display: c.key === "name" ? "flex" : undefined, alignItems: c.key === "name" ? "center" : undefined,
                    }}>
                      {c.key === "name" && groupMode !== "company" && (
                        <Caret
                          open={!isCollapsed}
                          visible={!!hasChildren}
                          onToggle={() => toggleCollapsed(String(item._id))}
                        />
                      )}
                      {c.key === "name" && !item.redacted ? (
                        <span style={{ marginRight: 6, display: "inline-flex" }}>
                          <ProgressCell item={item} isParent={parentIds.has(String(item._id))}
                            canEdit={mayEditProgress(item)} onSave={(v) => saveLeafProgress(item, v)} />
                        </span>
                      ) : null}
                      {c.key === "name" ? (
                        // Names are truncated to the column width, so the
                        // hover shows the whole thing (plus the parties on
                        // it, which are cut off even sooner).
                        <Tooltip
                          // Two columns: the row's own detail on the left,
                          // and on the right the coordination area it sits
                          // in, zoomed so the zone fills the frame.
                          overlayStyle={{ maxWidth: 600 }}
                          overlayInnerStyle={{ padding: 10 }}
                          title={(
                            <AreaTooltip areas={areasFor(item)}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>{text}</div>
                              {(areasFor(item) || []).map((a) => (
                                <div key={a._id} style={{ opacity: 0.85 }}>
                                  <span style={{
                                    display: "inline-block", width: 8, height: 8, borderRadius: 2,
                                    background: a.color || "#3b82f6", marginRight: 6,
                                  }} />
                                  {a.name}{a.code ? ` · ${a.code}` : ""}
                                  {(a.dates || []).map((d, i) => (
                                    <div key={i} style={{ marginLeft: 14, fontSize: 11, opacity: 0.8 }}>
                                      {d.date ? new Date(d.date).toLocaleDateString() : "date not readable"} — {d.label}
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </AreaTooltip>
                          )}
                          mouseEnterDelay={0.35}
                          placement="topLeft"
                        >
                          <span style={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {/* The zone's colour on the row itself, so it
                                reads when the bar is scrolled out of view. */}
                            {(areasFor(item)[0] || {}).color ? (
                              <span
                                title={areasFor(item).map((a) => a.name).join(", ")}
                                style={{
                                  width: 9, height: 9, borderRadius: 2, flex: "0 0 auto",
                                  background: areasFor(item)[0].color,
                                }}
                              />
                            ) : null}
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{text}</span>
                            {/* Work nobody has been given. A partner can
                                put their hand up for it; the claim names
                                the area and waits for the owner. */}
                            {item.claimable && typeof onClaim === "function" ? (
                              <span
                                role="button"
                                className="gantt-row-claim"
                                title="Ask to take this activity on"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => { e.stopPropagation(); onClaim(item); }}
                                style={{
                                  flex: "0 0 auto", fontSize: 10.5, fontWeight: 600, cursor: "pointer",
                                  color: "#fe6919", border: "1px solid #fe6919", borderRadius: 4,
                                  padding: "0 6px", lineHeight: "16px", whiteSpace: "nowrap",
                                }}
                              >
                                Claim
                              </span>
                            ) : null}
                            {item.claimPending ? (
                              <span
                                title={item.claimPendingMine
                                  ? "Your claim is with the project owner"
                                  : "Another company has asked for this activity"}
                                style={{
                                  flex: "0 0 auto", fontSize: 10.5, color: "#8c8c8c",
                                  border: "1px dashed #d9d9d9", borderRadius: 4,
                                  padding: "0 6px", lineHeight: "16px", whiteSpace: "nowrap",
                                }}
                              >
                                {item.claimPendingMine ? "Claim pending" : "Spoken for"}
                              </span>
                            ) : null}
                          </span>
                        </Tooltip>
                      ) : text}
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>

        {/* Scrollable chart */}
        <div
          ref={scrollRef}
          style={{ overflowX: "auto", flex: 1, cursor: panning ? "grabbing" : "grab", userSelect: panning ? "none" : undefined }}
          onMouseLeave={hoverOff}
          onMouseDown={beginPan}
          title={panning ? undefined : "Drag to pan left / right"}
        >
          <div style={{ position: "relative", width: chartW }}>
            <svg ref={svgRef} width={chartW} height={chartH} style={{ display: "block" }}>
              {/* Week gridlines + labels */}
              {weekLines.map((d) => {
                const x = xOf(d);
                return (
                  <g key={d.getTime()}>
                    <line x1={x} y1={HEADER_H} x2={x} y2={chartH} stroke="#f0f0f0" />
                    <text x={x + 4} y={18} fontSize={11} fill="#999">
                      {fmt(d)}
                    </text>
                  </g>
                );
              })}

              {/* Row backgrounds + marks */}
              {rows.map((row, idx) => {
                const y = HEADER_H + idx * ROW_H;
                if (row.group) {
                  return (
                    <g key={row.key}>
                      <rect x={0} y={y} width={chartW} height={ROW_H} fill="#fafafa" />
                      <line x1={0} y1={y + ROW_H} x2={chartW} y2={y + ROW_H} stroke="#f0f0f0" />
                    </g>
                  );
                }
                const { item } = row;
                const gid = String(item._id);
                const geom = geomById[gid];
                const cy = geom.cy;
                const done = item.status === "done" || !!item.disabled;
                const health = done
                  ? DONE_FILL
                  : HEALTH_FILL[item.healthStatus] || HEALTH_FILL.on_track;
                // The colour of the coordination area this row sits in,
                // taken from the area itself so the bar matches the
                // drawing. Health is not discarded for it - it moves to
                // the outline, so "which zone" and "is it in trouble"
                // are both still readable at a glance.
                const rowAreas = areasFor(item);
                const areaColor = rowAreas.length ? (rowAreas[0].color || "") : "";
                // Another trade's row arrives from the server as a bare
                // bar (utils/partnerScope redacts it there, so the name
                // never reaches this browser). Draw it flat grey: the
                // partner reads the SEQUENCE their work sits in without
                // reading whose it is or how it is going.
                const fill = item.redacted
                  ? "#d9d9d9"
                  : (areaColor && !done) ? areaColor : health;
                // A field row started before its BIM sign-off (and not
                // approved early) gets a thin red outline.
                const outOfSeq = !item.redacted && !done && item.gate && item.gate.state === "out_of_sequence";
                const barStroke = item.redacted
                  ? ""
                  : outOfSeq ? "#cf1322" : (areaColor && !done) ? health : "";

                const { start, end, inWindow, x1, x2 } = geom;
                const open = item.issueCounts?.open || 0;
                const badgeRed = (item.issueCounts?.open_behind || 0) > 0;

                const isDiamond =
                  isMilestone(item.type);
                const reportable =
                  !canDrag(item) &&
                  !!projectId &&
                  typeof canReportProgress === "function" &&
                  canReportProgress(item);
                const dragProps = canDrag(item)
                  ? {
                      style: { cursor: drag && drag.gid === gid ? "grabbing" : "grab" },
                      onMouseDown: (e) => beginDrag(e, item, "move"),
                    }
                  : {};

                let barEndX = 0;
                const marks = [];

                // The baseline bar: a thin grey rail under the live bar,
                // showing what the plan said when someone agreed to it.
                // Drawn FIRST so the current bar sits on top of it, and
                // only when it differs from where the row is now - a
                // baseline that matches adds a line and says nothing.
                if (item.baselineFinish) {
                  const bStart = startOfDay(new Date(item.baselineStart || item.baselineFinish));
                  const bEnd = startOfDay(new Date(item.baselineFinish));
                  const movedDays = Math.round((+startOfDay(new Date(end)) - +bEnd) / 86400000);
                  if (movedDays !== 0 && bEnd >= windowStart && bStart <= windowEnd) {
                    const bx1 = xOf(bStart > windowStart ? bStart : windowStart);
                    const bx2 = xOf(bEnd < windowEnd ? bEnd : windowEnd) + PX_PER_DAY;
                    marks.push(
                      <rect
                        key="baseline"
                        x={bx1}
                        y={cy + ROW_H / 2 - 5}
                        width={Math.max(bx2 - bx1, 3)}
                        height={3}
                        rx={1.5}
                        fill={movedDays > 0 ? "#d4380d" : "#389e0d"}
                        opacity={0.5}
                      >
                        <title>{`Baseline ${String(item.baselineStart || item.baselineFinish).slice(0, 10)} → ${String(item.baselineFinish).slice(0, 10)} · ${movedDays > 0 ? `${movedDays} day${movedDays === 1 ? "" : "s"} late` : `${-movedDays} day${movedDays === -1 ? "" : "s"} early`}`}</title>
                      </rect>
                    );
                  }
                }

                // The field's own forecast (the contractor's look-ahead,
                // utils/lookaheadForecast): a dashed violet rail ABOVE the
                // bar, so plan and forecast read together. Drawn only
                // when it differs from the bar.
                if (item.forecastStart || item.forecastFinish) {
                  const fS = startOfDay(new Date(item.forecastStart || item.forecastFinish));
                  const fE = startOfDay(new Date(item.forecastFinish || item.forecastStart));
                  const same = +fS === +startOfDay(new Date(start)) && +fE === +startOfDay(new Date(end));
                  if (!same && fE >= windowStart && fS <= windowEnd) {
                    const fx1 = xOf(fS > windowStart ? fS : windowStart);
                    const fx2 = xOf(fE < windowEnd ? fE : windowEnd) + PX_PER_DAY;
                    const lateDays = Math.round((+fE - +startOfDay(new Date(end))) / 86400000);
                    marks.push(
                      <rect
                        key="forecast"
                        x={fx1}
                        y={cy - ROW_H / 2 + 2}
                        width={Math.max(fx2 - fx1, 3)}
                        height={3}
                        rx={1.5}
                        fill="#722ed1"
                        opacity={0.7}
                        strokeDasharray="4 2"
                      >
                        <title>{`Look-ahead forecast ${fmt(fS)} → ${fmt(fE)}${lateDays > 0 ? ` · finishes ${lateDays} day${lateDays === 1 ? "" : "s"} after the plan` : lateDays < 0 ? ` · ${-lateDays} day${lateDays === -1 ? "" : "s"} before the plan` : ""}`}</title>
                      </rect>
                    );
                  }
                }
                // A drawing deadline: the date the shop drawing must be
                // in for the field start it feeds - a red flag.
                if (item.deadline) {
                  const dd = startOfDay(new Date(item.deadline));
                  if (dd >= windowStart && dd <= windowEnd) {
                    const fx = xOf(dd) + PX_PER_DAY / 2;
                    const missed = dd < today && item.status !== "done";
                    marks.push(
                      <g key="deadline">
                        <line x1={fx} y1={cy - ROW_H / 2 + 2} x2={fx} y2={cy + ROW_H / 2 - 2} stroke={missed ? "#cf1322" : "#d4380d"} strokeWidth={1.5} />
                        <polygon points={`${fx},${cy - ROW_H / 2 + 2} ${fx + 8},${cy - ROW_H / 2 + 5} ${fx},${cy - ROW_H / 2 + 8}`} fill={missed ? "#cf1322" : "#d4380d"}>
                          <title>{`Drawing due by ${fmt(dd)}${missed ? " · passed" : ""}`}</title>
                        </polygon>
                      </g>
                    );
                    if (fx + 9 > barEndX) barEndX = fx + 9;
                  }
                }

                if (isDiamond) {
                  // diamond at endDate
                  if (end >= windowStart && end <= windowEnd) {
                    const cx = xOf(end) + PX_PER_DAY / 2;
                    const r = 6;
                    marks.push(
                      <polygon
                        key="d"
                        points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`}
                        fill={fill}
                        {...dragProps}
                      />
                    );
                    barEndX = cx + r;
                  }
                } else if (isMeeting(item.type)) {
                  const occ = expandOccurrences(
                    { ...item, startDate: start, endDate: end },
                    windowStart,
                    windowEnd
                  );
                  occ.forEach((d, i) => {
                    const x = xOf(d) + PX_PER_DAY / 2;
                    marks.push(
                      <circle key={`c${i}`} cx={x} cy={cy} r={4.5} fill={fill} {...dragProps} />
                    );
                    if (x > barEndX) barEndX = x + 5;
                  });
                } else if (inWindow) {
                  // task bar clamped to window
                  const w = Math.max(x2 - x1, 4);
                  const pct = Math.round(Math.max(0, Math.min(100, Number(item.progress) || 0)));
                  marks.push(
                    <rect
                      key="b"
                      x={x1}
                      y={cy - 7}
                      width={w}
                      height={14}
                      rx={4}
                      fill={fill}
                      stroke={barStroke || undefined}
                      strokeWidth={barStroke ? (outOfSeq ? 1.5 : 2) : undefined}
                      opacity={done ? 0.7 : 0.9}
                      {...dragProps}
                      {...(reportable
                        ? {
                            style: { cursor: "pointer" },
                            onClick: (e) => {
                              e.stopPropagation();
                              openProgressEditor(item, x1, cy);
                            },
                          }
                        : {})}
                    >
                      <title>
                        {`${item.name} · ${fmt(start)} – ${fmt(end)} · ${pct}%` +
                          (reportable ? " — click to report progress" : "")}
                      </title>
                    </rect>
                  );
                  if (pct > 0) {
                    marks.push(
                      <rect
                        key="p"
                        x={x1}
                        y={cy + 3}
                        width={(w * pct) / 100}
                        height={3.5}
                        rx={1.75}
                        fill="rgba(0,0,0,0.32)"
                        pointerEvents="none"
                      />
                    );
                  }
                  if (canDrag(item)) {
                    // left-edge grip: expand / retract from the start
                    // (the finish stays where it is)
                    marks.push(
                      <rect
                        key="rzs"
                        x={x1 - 2}
                        y={cy - 8}
                        width={9}
                        height={16}
                        fill="transparent"
                        style={{ cursor: "ew-resize" }}
                        onMouseDown={(e) => beginDrag(e, item, "start")}
                      >
                        <title>Drag to change the start</title>
                      </rect>,
                      <line
                        key="rzsl"
                        x1={x1 + 3}
                        y1={cy - 5}
                        x2={x1 + 3}
                        y2={cy + 5}
                        stroke="rgba(255,255,255,0.75)"
                        strokeWidth={1.5}
                        pointerEvents="none"
                      />
                    );
                    // right-edge resize grip
                    marks.push(
                      <rect
                        key="rz"
                        x={x2 - 7}
                        y={cy - 8}
                        width={9}
                        height={16}
                        fill="transparent"
                        style={{ cursor: "ew-resize" }}
                        onMouseDown={(e) => beginDrag(e, item, "resize")}
                      />,
                      <line
                        key="rzl"
                        x1={x2 - 3}
                        y1={cy - 5}
                        x2={x2 - 3}
                        y2={cy + 5}
                        stroke="rgba(255,255,255,0.75)"
                        strokeWidth={1.5}
                        pointerEvents="none"
                      />
                    );
                  }
                  barEndX = x2;
                }

                // Sign-off star at the activity's sign-off date: gold
                // while it waits, green once signed, red when the date
                // has passed unsigned. Rows without a date need no
                // sign-off and get no star.
                // Where the sign-off was FIRST planned, once it has moved:
                // a grey star, drawn under the live one.
                if (item.signOffDateOriginal && item.signOffDate) {
                  const od = startOfDay(new Date(item.signOffDateOriginal));
                  if (od >= windowStart && od <= windowEnd) {
                    const ocx = xOf(od) + PX_PER_DAY / 2;
                    marks.push(
                      <polygon key="so0" points={starPoints(ocx, cy, 7.5, 3.2)} fill="#bfbfbf" stroke="#fff" strokeWidth={1} opacity={0.85} data-testid="signoff-original">
                        <title>{`Sign-off originally planned ${fmt(od)} - moved to ${fmt(startOfDay(new Date(item.signOffDate)))}`}</title>
                      </polygon>
                    );
                    if (ocx + 8 > barEndX) barEndX = ocx + 8;
                  }
                }
                if (item.signOffDate) {
                  const sd = startOfDay(new Date(item.signOffDate));
                  if (sd >= windowStart && sd <= windowEnd) {
                    const scx = xOf(sd) + PX_PER_DAY / 2;
                    const signed = !!item.signedOffAt;
                    const overdue = !signed && sd < today;
                    const dLeft = Math.round((+sd - +today) / 86400000);
                    marks.push(
                      <polygon
                        key="so"
                        points={starPoints(scx, cy, 7.5, 3.2)}
                        fill={signed ? "#2e7d32" : overdue ? "#d4380d" : "#f5b301"}
                        stroke="#fff"
                        strokeWidth={1}
                      >
                        <title>{signed
                          ? `Signed off ${String(item.signedOffAt).slice(0, 10)}${item.signedOffBy && item.signedOffBy.name ? ` by ${item.signedOffBy.name}` : ""} (planned ${fmt(sd)})${(forcedSignOff(item) || {}).short ? ` · ${forcedSignOff(item).short}` : ""}`
                          : `Sign-off due ${fmt(sd)} · ${overdue ? `${-dLeft} day${dLeft === -1 ? "" : "s"} overdue` : dLeft === 0 ? "due today" : `${dLeft} day${dLeft === 1 ? "" : "s"} left`}`}</title>
                      </polygon>
                    );
                    if (scx + 8 > barEndX) barEndX = scx + 8;
                  }
                }
                // THE OWNER/GC SIGN-OFF - a BLUE star (owner, 2026-10-21: "the
                // blue star ... to mark client sign off"), on the day the GC
                // picked or the day it was switched on. Drawn on the topmost
                // rows a level / area / system toggle covers (the server's
                // `star`), pale while its day is still to come. Beside the
                // activity star when both fall on one day, not on top of it.
                if (item.ownerSignOff && item.ownerSignOff.star && item.ownerSignOff.date) {
                  const od = startOfDay(new Date(`${item.ownerSignOff.date}T00:00:00`));
                  if (od >= windowStart && od <= windowEnd) {
                    const sameDay = item.signOffDate && +startOfDay(new Date(item.signOffDate)) === +od;
                    const bcx = xOf(od) + PX_PER_DAY / 2 + (sameDay ? 12 : 0);
                    const o = item.ownerSignOff;
                    marks.push(
                      <polygon
                        key="oso"
                        points={starPoints(bcx, cy, 7.5, 3.2)}
                        fill={o.planned ? "#93c5fd" : "#1d4ed8"}
                        stroke="#fff"
                        strokeWidth={1}
                        data-testid="owner-signoff-star"
                      >
                        <title>{`${o.planned ? "Owner/GC sign-off planned" : "Signed off by owner/GC"} ${fmt(od)} · ${o.scope}${o.by ? ` · marked by ${o.by}` : ""}`}</title>
                      </polygon>
                    );
                    if (bcx + 8 > barEndX) barEndX = bcx + 8;
                  }
                }

                return (
                  <g
                    key={item._id}
                    opacity={savingGid === gid ? 0.55 : 1}
                    onClick={() => {
                      if (suppressClickRef.current) {
                        suppressClickRef.current = false;
                        return;
                      }
                      if (onSelect) onSelect(item);
                    }}
                    style={{ cursor: onSelect ? "pointer" : "default" }}
                    onMouseEnter={hoverOn("item", { item })}
                    onMouseMove={hoverMove}
                    onMouseLeave={hoverOff}
                    onContextMenu={onContextAction ? (e) => openContext(e, item) : undefined}
                  >
                    <rect x={0} y={y} width={chartW} height={ROW_H} fill={hover?.item === item ? "#f5f9ff" : (idx % 2 ? "#fcfcfc" : "#fff")} />
                    <line x1={0} y1={y + ROW_H} x2={chartW} y2={y + ROW_H} stroke="#fafafa" />
                    {marks}
                    {(() => {
                      // Progress / issues pill after the bar: "62% · 3/5 issues"
                      // (+ ⏳ when a partner report awaits review). Live: the
                      // dashboard reloads the feed on every issue / progress
                      // event, so the numbers follow approvals in real time.
                      const c = item.issueCounts || {};
                      const total = (c.open || 0) + (c.resolved || 0) + (c.closed || 0);
                      const solved = (c.resolved || 0) + (c.closed || 0);
                      const pct = Number.isFinite(Number(item.progress)) ? Math.round(Number(item.progress)) : 0;
                      const pending = item.pendingProgress && item.pendingProgress.at;
                      const parts = [];
                      if (item.status === "done" || pct >= 100) parts.push("✓ 100%");
                      else if (pct > 0) parts.push(`${pct}%`);
                      if (total > 0) parts.push(`${solved}/${total} issue${total === 1 ? "" : "s"}${open ? ` (${open} open)` : ""}`);
                      if (pending) parts.push(`⏳ ${Math.round(item.pendingProgress.progress)}% reported`);
                      if (!parts.length) return null;
                      const tip = [
                        `${item.name}`,
                        `Progress: ${item.status === "done" ? "100% (done)" : `${pct}%`}${parentIds.has(String(item._id)) ? ` (${PARENT_TIP.toLowerCase()})` : ""}`,
                        total ? `Issues: ${solved} solved of ${total}${open ? `, ${open} open${badgeRed ? " (behind)" : ""}` : ""}` : "Issues: none linked",
                        pending ? `Awaiting review: ${item.pendingProgress.name || "partner"} reported ${Math.round(item.pendingProgress.progress)}% on ${new Date(item.pendingProgress.at).toLocaleString()}${item.pendingProgress.note ? ` - "${item.pendingProgress.note}"` : ""} (Review Center > Schedule)` : null,
                      ].filter(Boolean).join("\n");
                      const color = pending ? "#d48806" : badgeRed ? "#d4380d" : (item.status === "done" || pct >= 100) ? "#2e7d32" : "#8c8c8c";
                      return (
                        <text x={Math.min(barEndX + 6, chartW - 120)} y={cy + 4} fontSize={11} fill={color} fontWeight={badgeRed || pending ? 700 : 500}>
                          <title>{tip}</title>
                          {parts.join(" · ")}
                        </text>
                      );
                    })()}
                  </g>
                );
              })}

              {/* WEEKENDS - AND THE ORDER IS THE WHOLE POINT.
                  First attempt drew these as the FIRST children of the svg,
                  "behind everything". In SVG that means painted FIRST and
                  therefore covered by everything after - and every row paints
                  an OPAQUE full-width rect (#fff / #fcfcfc, line ~1482). So
                  134 bands rendered and 13 pixels of them survived: the
                  leader could not see them because they were underneath the
                  row striping, not because the colour was too light. I
                  darkened the colour first and fixed the wrong thing.
                  They now sit ABOVE the row backgrounds and BELOW the bars,
                  translucent so a bar crossing a weekend is tinted rather
                  than hidden. pointer-events off: this is paint, not a
                  target. */}
              {weekendBands.map((d) => (
                <rect key={`wk-${d.getTime()}`} x={xOf(d)} y={HEADER_H}
                      width={PX_PER_DAY} height={chartH - HEADER_H}
                      fill="#2b3a55" fillOpacity={0.09} pointerEvents="none" />
              ))}

              {/* Dependency arrows above the bars */}
              {arrows}

              {/* Due-date markers: issues (circles) and checklist items
                  (squares) on their linked plan rows. Red = open past
                  due, amber = open upcoming, green = resolved/complete,
                  blue = open checklist, grey = open but not approved to
                  resolve - never past due (back/utils/issueApproval). */}
              {rows.map((row, idx) => {
                if (row.group) return null;
                const gid = String(row.item._id);
                const y = HEADER_H + idx * ROW_H;
                const markers = [];
                for (const iss of issues) {
                  if (!issueOnActivity(iss, gid) || !iss.dueDate) continue;
                  const due = startOfDay(new Date(iss.dueDate));
                  if (isNaN(+due) || due < windowStart || due > windowEnd) continue;
                  const open = !["resolved", "abandoned"].includes(iss.status);
                  const unapproved = open && iss.approvedToResolve !== true;
                  const color = !open ? "#389e0d" : unapproved ? "#9ca3af" : due < today ? "#d4380d" : "#d48806";
                  markers.push(
                    <circle
                      key={`im-${iss._id}`}
                      cx={xOf(due) + PX_PER_DAY / 2}
                      cy={y + ROW_H - 7}
                      r={6}
                      fill={color}
                      stroke="#fff"
                      strokeWidth={1.5}
                      style={{ cursor: "pointer" }}
                      onMouseEnter={hoverOn("issue", { data: iss, item: row.item, color, due, open, unapproved })}
                      onMouseMove={hoverMove}
                      onMouseLeave={hoverOff}
                    />
                  );
                }
                for (const cl of checklistItems) {
                  if (String(cl.ganttItemId || "") !== gid || !cl.dueDate) continue;
                  const due = startOfDay(new Date(cl.dueDate));
                  if (isNaN(+due) || due < windowStart || due > windowEnd) continue;
                  const done = cl.status === "complete";
                  const color = done ? "#389e0d" : due < today ? "#d4380d" : "#2563EB";
                  markers.push(
                    <rect
                      key={`cm-${cl._id}`}
                      x={xOf(due) + PX_PER_DAY / 2 - 6}
                      y={y + ROW_H - 13}
                      width={12}
                      height={12}
                      rx={2}
                      fill={color}
                      stroke="#fff"
                      strokeWidth={1.5}
                      style={{ cursor: "pointer" }}
                      onMouseEnter={hoverOn("checklist", { data: cl, item: row.item, color, due, done })}
                      onMouseMove={hoverMove}
                      onMouseLeave={hoverOff}
                    />
                  );
                }
                return markers.length ? <g key={`mk-${row.key}`}>{markers}</g> : null;
              })}

              {/* Today line — drawn last so it sits on top */}
              <line
                x1={xOf(today) + PX_PER_DAY / 2}
                y1={HEADER_H - 6}
                x2={xOf(today) + PX_PER_DAY / 2}
                y2={chartH}
                stroke="#1677ff"
                strokeWidth={2}
                strokeDasharray="4 3"
              />
              <text
                x={xOf(today) + PX_PER_DAY / 2 + 4}
                y={HEADER_H - 10}
                fontSize={10}
                fill="#1677ff"
                fontWeight={600}
              >
                Today
              </text>
            </svg>

            {/* The date strip again, on top, moved down with the scroll (see
                STICKY HEADER). It never takes a click. */}
            <svg ref={rightHeadRef} data-testid="gantt-sticky-dates" width={chartW} height={HEADER_H}
                 style={{ position: "absolute", left: 0, top: 0, zIndex: 3, pointerEvents: "none", display: "block" }}>
              <rect x={0} y={0} width={chartW} height={HEADER_H} fill="#fff" />
              <line x1={0} y1={HEADER_H - 0.5} x2={chartW} y2={HEADER_H - 0.5} stroke="#f0f0f0" />
              {weekLines.map((d) => (
                <text key={d.getTime()} x={xOf(d) + 4} y={18} fontSize={11} fill="#999">{fmt(d)}</text>
              ))}
              <line x1={xOf(today) + PX_PER_DAY / 2} y1={HEADER_H - 6} x2={xOf(today) + PX_PER_DAY / 2} y2={HEADER_H}
                    stroke="#1677ff" strokeWidth={2} strokeDasharray="4 3" />
              <text x={xOf(today) + PX_PER_DAY / 2 + 4} y={HEADER_H - 10} fontSize={10} fill="#1677ff" fontWeight={600}>Today</text>
            </svg>

            {/* Contractor progress popover (hand-rolled, stays inside the
                scrollable chart so it tracks horizontal scrolling) */}
            {progressEditor && (
              <div
                style={{
                  position: "absolute",
                  left: progressEditor.x,
                  top: Math.min(progressEditor.y, Math.max(chartH - 130, 0)),
                  width: 240,
                  background: "#fff",
                  border: "1px solid #d9d9d9",
                  borderRadius: 8,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  padding: "10px 12px",
                  zIndex: 5,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {progressEditor.name}
                </div>
                <div style={{ fontSize: 12, color: "#888" }}>
                  Progress: {progressEditor.value}%
                </div>
                <Slider
                  min={0}
                  max={100}
                  step={5}
                  value={progressEditor.value}
                  onChange={(v) =>
                    setProgressEditor((p) => (p ? { ...p, value: v } : p))
                  }
                />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <Button size="small" onClick={() => setProgressEditor(null)}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    loading={savingGid === progressEditor.gid}
                    onClick={saveProgress}
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hover card - follows the mouse inside the chart. Kinds: plan
          item (task/milestone/meeting...), issue marker, checklist marker.
          Header swatch = bar/marker colour; contractor shows its lane colour. */}
      {hover && !drag && (() => {
        const kind = hover.kind || "item";
        const it = hover.item;
        // The card carries the drawing when the row sits in a
        // coordination area, so it needs room for two columns. This is
        // the card the page means by "hover a bar for details" - putting
        // the area only on the name cell was why it never appeared here.
        const hoverAreas = hover.item ? areasFor(hover.item) : [];
        const cardW = hoverAreas.length ? 600 : 360;
        const rootW = hover.w || 800;
        // HoverCard places itself around the pointer and keeps itself
        // inside the chart on all four sides — see its definition for why
        // the bottom rows used to flicker.
        const box = (children, accent) => (
          <HoverCard x={hover.x} y={hover.y} cardW={cardW} rootW={rootW} rootH={hover.rootH} accent={accent}>
            {children}
          </HoverCard>
        );
        const Row = ({ k, v }) => (v === undefined || v === null || v === "" ? null : (<><span style={{ color: "#888" }}>{k}</span><span>{v}</span></>));
        const grid = { display: "grid", gridTemplateColumns: "auto 1fr", gap: "3px 10px", fontSize: 12.5 };
        const compName = it ? nameOfCompany(it.responsibleCompanyId) || (it.responsibleCompanyId ? "Partner company" : "") : "";
        const compColor = it ? colorOfCompany(it.responsibleCompanyId) : "";
        const compCell = compName ? (
          <span style={{ fontWeight: 600 }}>
            {compColor ? <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: compColor, marginRight: 6, verticalAlign: "middle" }} /> : null}{compName}
          </span>
        ) : null;

        if (kind === "issue" && hover.data) {
          const d = hover.data;
          const label = d.displayId ? `${issueLabel(d)}` : "Issue";
          return box(
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ width: 12, height: 12, borderRadius: "50%", background: hover.color, flexShrink: 0 }} />
                <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.25, wordBreak: "break-word" }}>{label}: {d.title}</div>
              </div>
              <div style={grid}>
                <Row k="Kind" v="Issue due date" />
                <Row k="Due" v={<span>{fmtLong(hover.due)}{hover.unapproved ? <span style={{ color: "#6b7280" }}> · not approved to resolve</span> : hover.open && hover.due < today ? <b style={{ color: "#d4380d" }}> · overdue</b> : ""}</span>} />
                <Row k="Status" v={String(d.status || "open").replace(/_/g, " ")} />
                <Row k="Priority" v={d.priority} />
                <Row k="Activity" v={it?.name} />
                {compCell ? <Row k="Contractor" v={compCell} /> : null}
                <Row k="Assigned" v={(d.responsibleUsers || []).map((r) => { const u = r?.userId && typeof r.userId === "object" ? r.userId : r; return [u?.name, u?.lastName].filter(Boolean).join(" ") || u?.email; }).filter(Boolean).join(", ") || undefined} />
              </div>
            </>, hover.color);
        }
        if (kind === "checklist" && hover.data) {
          const d = hover.data;
          return box(
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: hover.color, flexShrink: 0 }} />
                <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.25, wordBreak: "break-word" }}>{d.title}</div>
              </div>
              <div style={grid}>
                <Row k="Kind" v="Checklist item" />
                <Row k="Due" v={<span>{fmtLong(hover.due)}{!hover.done && hover.due < today ? <b style={{ color: "#d4380d" }}> · past due</b> : ""}</span>} />
                <Row k="Status" v={String(d.status || "open").replace(/_/g, " ")} />
                <Row k="With" v={d.currentAssignee?.name || (d.recipients || []).map((r) => r.name).join(", ") || undefined} />
                <Row k="Activity" v={it?.name} />
                {compCell ? <Row k="Contractor" v={compCell} /> : null}
                {d.notes ? <Row k="Notes" v={String(d.notes).slice(0, 160)} /> : null}
              </div>
            </>, hover.color);
        }
        if (!it) return null;
        const { start, end } = effDates(it);
        const dur = dayDiff(end, start) + 1;
        const open = (it.issueCounts && (it.issueCounts.open || 0) + (it.issueCounts.pending || 0) + (it.issueCounts.under_review || 0)) || 0;
        const total = it.issueCounts ? Object.values(it.issueCounts).reduce((a, b) => a + (Number(b) || 0), 0) : 0;
        const preds = (it.dependencies || []).map((d) => itemById.get(depId(d))).filter(Boolean);
        const late = it.status !== "done" && end < today;
        const health = it.healthStatus ? String(it.healthStatus).replace(/_/g, " ") : "";
        const isMs = isMilestone(it.type);
        const accent = (it.status === "done" || it.disabled) ? DONE_FILL : (HEALTH_FILL[it.healthStatus] || (compColor || "#1E88E5"));
        return box(
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 auto", minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              {isMs
                ? <span style={{ width: 12, height: 12, background: accent, transform: "rotate(45deg)", flexShrink: 0, borderRadius: 2 }} />
                : <span style={{ width: 14, height: 8, borderRadius: 3, background: accent, flexShrink: 0 }} />}
              <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.25, wordBreak: "break-word" }}>{it.wbs ? `${it.wbs} ` : ""}{it.name}</div>
            </div>
            <div style={grid}>
              {it.disabled ? <Row k="State" v={<span style={{ color: "#8c8c8c", fontWeight: 600 }}>Disabled - not on the emailed schedule; linked issues / checklists abandoned</span>} /> : null}
              {it.updatedAt ? <Row k="Last modified" v={<span>{fmtLong(new Date(it.updatedAt))}{it.lastEditedBy?.name ? <span style={{ color: "#888" }}> by {it.lastEditedBy.name}</span> : null}</span>} /> : null}
              <Row k="Type" v={TYPE_LABEL[it.type] || it.type || "Task"} />
              {isMs
                ? <Row k="Date" v={fmtLong(end)} />
                : <><Row k="Start" v={fmtLong(start)} /><Row k="End" v={<span>{fmtLong(end)}{late ? <b style={{ color: "#d4380d" }}> · overdue</b> : ""}</span>} /><Row k="Duration" v={`${dur} day${dur === 1 ? "" : "s"}`} /></>}
              <Row k="Status" v={`${STATUS_LABEL[it.status] || it.status || "Planned"}${health ? ` · ${health}` : ""}`} />
              <Row k="Progress" v={parentIds.has(String(it._id))
                ? (pctDisplay(it, true).unknown ? "no data entered" : `${pctDisplay(it, true).text} · ${pctDisplay(it, true).tip.toLowerCase()}`)
                : pctDisplay(it, false).unknown ? "no data entered" : pctDisplay(it, false).sheets ? `${pctText(it.progress)} · ${pctDisplay(it, false).tip.toLowerCase()}` : `${pctText(it.progress)}${it.progressBy && it.progressBy.name && it.progressBy.source !== "rollup" ? ` · set by ${it.progressBy.name}` : ""}${it.progressAt ? ` on ${fmtLong(new Date(it.progressAt))}` : ""}`} />
              {it.signOffDate ? (
                <Row k="Sign-off" v={it.signedOffAt
                  ? <span style={{ color: "#2e7d32", fontWeight: 600 }}>★ signed off {fmtLong(new Date(it.signedOffAt))}{it.signedOffBy && it.signedOffBy.name ? <span style={{ color: "#888", fontWeight: 400 }}> by {it.signedOffBy.name}</span> : null}{(() => {
                    // THE GATE WAS OVERRIDDEN, SAID WHERE THE PROJECT
                    // MANAGER LOOKS. Without this the only people who
                    // could see it were reading a shop-drawing register
                    // or an activity log.
                    const f = forcedSignOff(it);
                    return f ? <span style={{ color: "#B45309", fontWeight: 600 }}> · {f.short}{f.by ? ` (${f.by})` : ""}</span> : null;
                  })()}</span>
                  : (() => {
                    const d = Math.round((+startOfDay(new Date(it.signOffDate)) - +today) / 86400000);
                    return <span>★ due {fmtLong(new Date(it.signOffDate))}{d < 0 ? <b style={{ color: "#d4380d" }}> · {-d} day{d === -1 ? "" : "s"} overdue</b> : <span style={{ color: "#8A6112" }}> · {d === 0 ? "due today" : `${d} day${d === 1 ? "" : "s"} left`}</span>}</span>;
                  })()} />
              ) : null}
              {it.ownerSignOff ? (
                <Row k="Owner/GC" v={<span style={{ color: it.ownerSignOff.planned ? "#3b82f6" : "#1d4ed8", fontWeight: 600 }} data-testid="owner-signoff-row">★ {it.ownerSignOff.planned ? "sign-off planned" : "signed off"} {fmtLong(new Date(`${it.ownerSignOff.date}T00:00:00`))}<span style={{ color: "#888", fontWeight: 400 }}> · {it.ownerSignOff.scope}{it.ownerSignOff.inherited ? " (above this row)" : ""}{it.ownerSignOff.by ? ` · by ${it.ownerSignOff.by}` : ""}</span></span>} />
              ) : null}
              {(it.forecastStart || it.forecastFinish) ? (
                <Row k="Forecast" v={<span style={{ color: "#722ed1" }}>{fmtLong(new Date(it.forecastStart || it.forecastFinish))} → {fmtLong(new Date(it.forecastFinish || it.forecastStart))}<span style={{ color: "#888" }}> · contractor's look-ahead{it.forecastSource && it.forecastSource.asOf ? ` of ${fmtLong(new Date(it.forecastSource.asOf))}` : ""}</span></span>} />
              ) : null}
              {/* A LEVEL-WIDE look-ahead window (one per level, owner
                  2026-10-13: "somewhere none combersome") keeps the lines
                  it spans; they are read here on hover, not drawn. */}
              {(it.forecastStart || it.forecastFinish) && it.forecastSource && Array.isArray(it.forecastSource.lines) && it.forecastSource.lines.length ? (
                <Row k="Look-ahead lines" v={<span data-testid="forecast-lines" style={{ color: "#722ed1" }}>{it.forecastSource.lines.slice(0, 10).map((l, n) => (
                  <span key={`${l.code}-${n}`} style={{ display: "block" }}>{l.desc || l.system} <span style={{ color: "#888" }}>{fmt(ymdLocal(l.forecastStart))} → {fmt(ymdLocal(l.forecastFinish))}</span></span>
                ))}{it.forecastSource.lines.length > 10 ? <span style={{ color: "#888" }}>+{it.forecastSource.lines.length - 10} more</span> : null}</span>} />
              ) : null}
              {it.deadline ? (
                <Row k="Drawing due" v={<span style={{ color: "#d4380d" }}>{fmtLong(new Date(it.deadline))}{startOfDay(new Date(it.deadline)) < today && it.status !== "done" ? <b> · passed</b> : null}</span>} />
              ) : null}
              {it.gate && gateTag(it.gate) ? (() => {
                const g = gateTag(it.gate);
                const hex = { green: "#389e0d", blue: "#1677ff", gold: "#d48806", orange: "#d46b08", red: "#cf1322" }[g.color] || "#8c8c8c";
                return <Row k="Gate" v={<span><b style={{ color: hex }}>{g.text}</b>{it.gate.signOffName ? <span style={{ color: "#888" }}> · {it.gate.signOffName}</span> : null}{(() => { const eb = it.gate.earlyBy; const n = eb && (eb.name || (typeof eb === "string" && !/^[0-9a-f]{24}$/i.test(eb) ? eb : "")); return n ? <span style={{ color: "#888" }}> · approved by {n}</span> : null; })()}</span>} />;
              })() : null}
              {(it.phase || it.workPackage) ? <Row k="Package" v={[PHASE_LABEL[it.phase] || it.phase, it.step, it.workPackage].filter(Boolean).join(" · ")} /> : null}
              {compCell ? <Row k="Contractor" v={compCell} /> : null}
              {(it.responsibleUsers && it.responsibleUsers.length) ? (
                <Row k={it.responsibleUsers.length > 1 ? "Responsible people" : "Responsible"} v={<span>{it.responsibleUsers.map((u, i) => <span key={u.userId || i} style={{ display: "block", fontWeight: 600 }}>{u.name || u.email}{u.email && u.name ? <span style={{ color: "#888", fontWeight: 400 }}> - {u.email}</span> : null}</span>)}</span>} />
              ) : it.responsibleUserName ? <Row k="Responsible" v={<span style={{ fontWeight: 600 }}>{it.responsibleUserName}{it.responsibleUserEmail ? <span style={{ color: "#888", fontWeight: 400 }}> - {it.responsibleUserEmail}</span> : null}</span>} /> : null}
              {(it.discipline || it.areaKey || it.level || it.grid) ? <Row k="Where" v={[it.discipline, it.areaKey, it.level, it.grid].filter(Boolean).join(" · ")} /> : null}
              {total > 0 ? <Row k="Issues" v={<span>{open > 0 ? <b style={{ color: "#d4380d" }}>{open} open</b> : "none open"} · {total} total</span>} /> : null}
              {/* What the people on this activity's issues report, next
                  to what the plan says. The two disagreeing is the point:
                  a plan at 90% whose issues report 40% is the sentence
                  this row exists to say. */}
              {/* Why a partner is seeing somebody else's line: they have
                  issues on it. Without saying so the row looks like a
                  scoping mistake. */}
              {it.viaIssues ? (
                <Row
                  k="On your plan"
                  v={<span style={{ color: "#8A6112" }}>because you have issues on this activity</span>}
                />
              ) : null}
              {Number.isFinite(Number(it.issueProgress)) && it.issueProgressCount > 0 ? (
                <Row
                  k="Reported"
                  v={
                    <span title={`Average progress to completion across ${it.issueProgressCount} issue${it.issueProgressCount === 1 ? "" : "s"} on this activity`}>
                      <b style={{ color: it.issueProgress >= 100 ? "#317A1B" : "#1F4E63" }}>{it.issueProgress}%</b>
                      <span style={{ color: "#888" }}> from {it.issueProgressCount} issue{it.issueProgressCount === 1 ? "" : "s"}</span>
                      {Number.isFinite(Number(it.progress)) && Math.abs(Number(it.progress) - Number(it.issueProgress)) >= 15 ? (
                        <span style={{ color: "#B84B0A" }}> · plan says {it.progress}%</span>
                      ) : null}
                    </span>
                  }
                />
              ) : null}
              {/* The FIELD's own number, counted from tracked model
                  elements (owner's rule: issues are BIM, construction
                  tracking is the field). Never a typed percentage. */}
              {it.phase === "field" && Number.isFinite(Number(it.trackingProgress)) && it.trackingProgressCount > 0 ? (
                <Row
                  k="Field"
                  v={
                    <span title={`Counted from ${it.trackingProgressCount} tracked model element${it.trackingProgressCount === 1 ? "" : "s"} on this activity`}>
                      <b style={{ color: it.trackingProgress >= 100 ? "#317A1B" : "#1F4E63" }}>{it.trackingProgress}%</b>
                      {/* A system task (owner, 2026-10-26): installed of its
                          tracked elements, "X / total", like issues. */}
                      {Number.isFinite(Number(it.trackingInstalledCount)) && it.progressBy && it.progressBy.source === "tracking"
                        ? <span style={{ color: "#888" }}> · {Number(it.trackingInstalledCount).toLocaleString("en-US")} / {Number(it.trackingProgressCount).toLocaleString("en-US")} installed</span>
                        : <span style={{ color: "#888" }}> from {it.trackingProgressCount} element{it.trackingProgressCount === 1 ? "" : "s"}</span>}
                      {Number.isFinite(Number(it.progress)) && Math.abs(Number(it.progress) - Number(it.trackingProgress)) >= 15 ? (
                        <span style={{ color: "#B84B0A" }}> · plan says {it.progress}%</span>
                      ) : null}
                    </span>
                  }
                />
              ) : null}
              {preds.length ? <Row k="After" v={preds.map((p) => p.name).join(", ")} /> : null}
              {it.actualStart ? <Row k="Actual start" v={fmtLong(startOfDay(new Date(it.actualStart)))} /> : null}
              {it.actualEnd ? <Row k="Actual end" v={fmtLong(startOfDay(new Date(it.actualEnd)))} /> : null}
            </div>
            {(canDrag(it) || (canReportProgress && canReportProgress(it))) && (
              <div style={{ marginTop: 8, fontSize: 11, color: "#999" }}>
                {canDrag(it) ? (onContextAction ? "Drag the bar to move · drag either end to change the start or finish · right-click for more" : "Drag the bar to move · drag its right edge to change the end date") : "Click the bar to report progress"}
              </div>
            )}
          </div>
          {hoverAreas.length > 0 && (
            // The zone this activity sits in, zoomed so it fills the
            // frame rather than showing as a speck on the whole sheet.
            <div style={{ flex: "0 0 auto" }}>
              {hoverAreas.slice(0, 1).map((a) => (
                <AreaShapePreview key={a._id} area={a} zoom width={230} height={165} />
              ))}
              {hoverAreas.length > 1 && (
                <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                  +{hoverAreas.length - 1} more area{hoverAreas.length - 1 === 1 ? "" : "s"}
                </div>
              )}
            </div>
          )}
          </div>, accent);
      })()}
      {ctxMenu ? (
        <GanttContextMenu menu={ctxMenu} onClose={() => setCtxMenu(null)}
          onPick={(action) => { const m = ctxMenu; setCtxMenu(null); onContextAction(String(m.item._id), action, { date: m.date }); }} />
      ) : null}
    </div>
  );
}
